import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Headphones, Plus, X, RefreshCw, Loader, Trash2, ExternalLink, Save, Search,
  FileText, User, Calendar, AlertTriangle, Clock, Check, MessageSquare, HelpCircle,
  UploadCloud, ListChecks, CheckCircle2, Package, Camera, Send, ScanLine, Wrench,
  MapPin, StickyNote, UserPlus, GraduationCap, Eye, EyeOff, ShieldAlert, Smartphone, Mail,
  PackageCheck, Accessibility, Video,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { CATALOG, customQuestionId, pendingQuestions } from '../utils/caseQuestionnaire';
import { caseDueMeta } from '../utils/caseDue';
import { scanState, toDate } from '../utils/caseScan';
import { uprightJpegBytes } from '../utils/imageOrient';
import { checkVideo, videoDuration, putFile, fmtDuration, fmtMB } from '../utils/issueVideo';
import PushAlertsCard from '../components/PushAlertsCard';
import { CustomerCaseCard, AssistCaseCard, DeliveryRequestCard, previewVerdict } from '../components/CustomerCasePortalCard';
import { registerSW, syncAlerts, setTileBadge } from '../utils/pushAlerts';

const MAX_MB = 4; // base64 inflates ~33%; Netlify function body limit is 6 MB

function arrayBufferToBase64(ab) {
  const bytes = new Uint8Array(ab);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
}
async function fileToBase64(file) {
  return { base64: arrayBufferToBase64(await file.arrayBuffer()), contentType: file.type || 'application/octet-stream', name: file.name };
}
const isAllowedFile = (f) => /pdf$/i.test(f.type) || /^image\//i.test(f.type);

// ── Customer Cases ──────────────────────────────────────────────────────────
// Admin register of AP Customer Experience cases. The inbound webhook reads each AP
// email + PDF into customer_cases (with Claude-drafted driver questions); this page lets
// an admin triage it — confirm the driver, tweak the questions, and release it. The
// escalating driver-portal reminder (ack → end-of-shift → carry-over → onboard → 2pm SMS)
// lands in Phase 2. Nothing goes back to Australia Post automatically.

const URL = '/.netlify/functions/customer-cases-admin-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const CATEGORY = {
  wrong_address: { label: 'Wrong address', color: '#d97706' },
  no_attempt:    { label: 'No delivery attempt', color: '#dc2626' },
  not_received:  { label: 'Not received', color: '#dc2626' },
  signature:     { label: 'Signature query', color: '#2563eb' },
  damaged:       { label: 'Damaged', color: '#b45309' },
  safe_drop:     { label: 'Safe drop', color: '#0891b2' },
  conduct:       { label: 'Conduct complaint', color: '#be123c' },
  redelivery:    { label: 'Redelivery', color: '#7c3aed' },
  // A sender recall is the OPPOSITE of a redelivery: the parcel is not to be delivered at
  // all, it comes back to the depot for return. Read on a phone as "Redelivery" it said the
  // one thing the office needs the driver not to do (Paul, 4 Sep 2026).
  rts:           { label: 'Return to sender (RTS)', color: '#0d9488' },
  other:         { label: 'Other', color: 'var(--hp-dim)' },
};
const cat = (k) => CATEGORY[k] || CATEGORY.other;

const STATUS_COLORS = { open: '#d97706', resolved: '#16a34a', closed: 'var(--hp-dim)', cancelled: '#b91c1c' };

// Delivery areas, as they come back on each stats row (from driver_routes).
const AREA_LABEL = { CITY: 'City', DARL: 'Darlinghurst', BWAY: 'Broadway', UNASSIGNED: 'Unassigned' };
const AREA_COLOR = { CITY: '#2563eb', DARL: '#16a34a', BWAY: '#7c3aed', UNASSIGNED: 'var(--hp-dim)' };
const areaLabel = (a) => AREA_LABEL[a] || a || 'Unassigned';

// Office fault-determination reason codes.
const FAULT_NO = [
  'Correct delivery attempt',
  'Knock and call out procedure followed',
  'Safe Drop compliant',
  'Special request',
  'Delivered as addressed',
  'Pre-existing arrangement',
  'Incorrect address on the parcel',
  'Delivered first time — customer located the parcel',
  'Other — specify',
  'Close the case — resolved',
];
const FAULT_YES = [
  'Delivered to incorrect address',
  'Incorrect scanning',
  'No attempt delivery',
  'Bad image capture',
  'Unsure what happened',
  'Other — specify',
];
const suggestion = (r) => (r && r.analysis && r.analysis.matchSuggestion) || {};

// Gmail linkifies the address in AP's subject line, so older case rows hold
// `… AUSTRALIA<https://www.google.com/maps/search/…>`. New cases are stripped at ingest
// (_shared/customer-case-parse); this keeps the ones already stored readable.
const cleanAddress = (s) => (s || '').toString()
  .replace(/<https?:\/\/[^>]*>/gi, ' ')
  .replace(/https?:\/\/\S+/gi, ' ')
  .replace(/\s*,\s*/g, ', ')
  .replace(/\s{2,}/g, ' ')
  .replace(/[\s,]+$/, '')
  .trim();

// The recovery brief the office sends with a case when a SECOND driver has to go and find
// the parcel. Pre-written from the case so it's one tap on a phone — always editable.
const defaultAssistTask = (r) => {
  if (!r) return '';
  const where = cleanAddress(r.addressee_address || r.customer_address);
  const art = r.tracking_no ? ` (article ${r.tracking_no})` : '';
  const lines = [`Please search for this parcel${art} at ${where || 'the address'} today.`];
  if (['not_received', 'safe_drop', 'wrong_address', 'signature'].includes(r.case_category)) {
    lines.push('Ask reception / concierge / the mailroom, check the parcel room and any safe-drop spot, and check the neighbouring units.');
  }
  lines.push('Photograph the parcel if you find it, and tell us where it was. If it is not there, say what you checked.');
  return lines.join(' ');
};

const shortDay = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—');

// Business-hours clock for a case: time only accrues Mon–Fri 09:00–17:00 (local = Sydney
// for HPR). Distinct from the driver-portal SLA countdown (due_at). Used to show how long
// the office has actually had a case in working time.
const BH_OPEN = 9, BH_CLOSE = 17;
const inBusinessHours = (d = new Date()) => { const day = d.getDay(); const h = d.getHours() + d.getMinutes() / 60; return day >= 1 && day <= 5 && h >= BH_OPEN && h < BH_CLOSE; };
function businessMsBetween(startMs, endMs) {
  if (!(endMs > startMs)) return 0;
  let total = 0;
  const d = new Date(startMs); d.setHours(0, 0, 0, 0);
  for (let t = d.getTime(); t <= endMs; t += 86400000) {
    const day = new Date(t).getDay();
    if (day === 0 || day === 6) continue;               // Sun / Sat
    const ws = new Date(t); ws.setHours(BH_OPEN, 0, 0, 0);
    const we = new Date(t); we.setHours(BH_CLOSE, 0, 0, 0);
    const s = Math.max(startMs, ws.getTime()), e = Math.min(endMs, we.getTime());
    if (e > s) total += e - s;
  }
  return total;
}
const fmtWorked = (ms) => {                              // business day = 8h (9–5)
  const min = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(min / 60), m = min % 60;
  const bd = Math.floor(h / 8), rh = h % 8;
  return bd > 0 ? `${bd}d ${rh}h ${m}m` : `${h}h ${m}m`;
};

// Countdown to the SLA target. Hidden while there's plenty of time; appears amber as it
// gets close and red once it's near/overdue — the "ticking clock" the case carries.
// Counted in WORKING days by the shared helper, so a weekend or a public holiday never
// inflates how late a case looks. Same numbers the driver's portal shows; only the palette
// is this page's (see caseDue.js).
function dueMeta(due_at) {
  const m = caseDueMeta(due_at);
  if (!m) return null;
  const color = m.overdue || m.days === 0 ? '#dc2626' : m.days === 1 ? '#d97706' : 'var(--hp-dim)';
  return { ...m, color };
}

// When the case arrived: AP's own case timestamp when the PDF gave one, else the
// moment the engine ingested it. Short AU format; year only when it differs.
// AP's own "Created Date" off the case detail page — when AP opened the case, which is not
// when they sent it to us. The row's created_date is the investigation form's cover DATE (the
// day it reached us) and that's what the SLA runs on; this is kept so a case AP sat on for a
// week doesn't read as a week of our silence. Lives on the analysis — no column for it.
const apOpened = (r) => (r && r.analysis && r.analysis.fields && r.analysis.fields.ap_created_date) || '';
// Only worth saying when the gap is real: shown once AP held the case a day or more.
function apOpenedNote(r) {
  const ap = apOpened(r);
  const got = r && (r.created_date || r.created_at);
  if (!ap || !got) return '';
  const lag = (new Date(got).getTime() - new Date(ap).getTime()) / 86400000;
  if (!(lag >= 1)) return '';
  return `AP opened this case ${Math.round(lag)} day${Math.round(lag) === 1 ? '' : 's'} before sending it to us`;
}

function fmtReceived(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const opts = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return `${d.toLocaleDateString('en-AU', opts)}, ${d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`;
}

function DueChip({ due_at, force }) {
  const [, tick] = useState(0);
  useEffect(() => { const iv = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(iv); }, []);
  const m = dueMeta(due_at);
  if (!m || (!m.show && !force)) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.7rem', fontWeight: 700, color: m.color, fontFamily: 'var(--font-mono)' }}>
      <Clock size={11} style={m.overdue ? { animation: 'spin 3s linear infinite' } : undefined} /> {m.label}
    </span>
  );
}

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%' };

// Driver picker: type any part of a login or name and pick from what's left. A native
// <select> of ~40 drivers meant scrolling for one login, and on an iPhone it can't be typed
// into at all (Paul, 17 Sep 2026). Logins that START with what's typed come first, so
// "GAR" puts GARCIARIP at the top. Enter picks the highlighted row, Esc backs out.
function DriverPicker({ drivers, value, onChange, placeholder = 'Type a login or name…', style }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const listRef = useRef(null);
  const label = (d) => (d.name ? `${d.login} · ${d.name}` : d.login);
  const sel = drivers.find((d) => d.login === value);
  const needle = q.trim().toUpperCase();
  const matches = !needle ? drivers : [
    ...drivers.filter((d) => d.login.startsWith(needle)),
    ...drivers.filter((d) => !d.login.startsWith(needle)
      && (d.login.includes(needle) || (d.name || '').toUpperCase().includes(needle))),
  ];
  const pick = (d) => { onChange(d.login); setOpen(false); setQ(''); };
  useEffect(() => { setHi(0); }, [needle]);
  useEffect(() => {
    const el = listRef.current && listRef.current.children[hi];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && matches[hi]) pick(matches[hi]); }
    else if (e.key === 'Escape') { setOpen(false); setQ(''); }
  };
  return (
    <div style={{ position: 'relative', ...style }}>
      <input className="input" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }}
        value={open ? q : (sel ? label(sel) : (value || ''))}
        placeholder={sel ? label(sel) : placeholder}
        autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck={false}
        onFocus={() => { setOpen(true); setQ(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={onKey} />
      {open && (
        <div ref={listRef} role="listbox"
          style={{ position: 'absolute', zIndex: 50, left: 0, right: 0, top: '100%', marginTop: 4, maxHeight: 260, overflowY: 'auto', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
          {matches.length === 0
            ? <div style={{ padding: '8px 10px', fontSize: '.82rem', color: 'var(--hp-dim)' }}>No driver matches “{q}”</div>
            : matches.map((d, i) => (
              <div key={d.login} role="option" aria-selected={d.login === value}
                onMouseDown={(e) => { e.preventDefault(); pick(d); }}
                onMouseEnter={() => setHi(i)}
                style={{ padding: '8px 10px', fontSize: '.85rem', cursor: 'pointer', background: i === hi ? 'rgba(212,175,55,0.18)' : 'transparent', fontWeight: d.login === value ? 700 : 400 }}>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{d.login}</span>
                {d.name && <span style={{ color: 'var(--hp-dim)' }}> · {d.name}</span>}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
const th = { padding: '9px 10px', textAlign: 'left', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' };
const td = { padding: '10px', borderTop: '1px solid var(--hp-border)', fontSize: '.86rem', verticalAlign: 'middle' };

const Badge = ({ text, color }) => (
  <span style={{ fontSize: '.68rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.05em', color, border: `1px solid ${color}`, borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{text}</span>
);

const emptyFields = () => ({
  case_number: '', enquiry_sub_type: '', case_category: 'other', contract: 'N22000', tracking_no: '',
  customer_name: '', customer_role: 'receiver', customer_address: '', customer_message: '', summary: '',
  description: '', ap_driver_login: '', signature_required: null, sla_target_date: '', created_date: '',
  driver_questions: [], quick_answers: [], key_facts: [],
});

// Repairs cases whose PDF never parsed (e.g. the Anthropic account ran out of
// credits mid-ingest) or that a pre-guard reply downgraded. Only shows when
// such rows exist. Loops the server action (a few PDFs per call to fit the 10s
// gateway) until nothing is left or a call makes no progress.
function RepairExtractionButton({ rows, onDone, notify }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const broken = rows.filter((r) => r.attachment_path && (r.parse_error || !r.case_number)).length;
  if (!broken && !busy) return null;

  const run = async () => {
    setBusy(true);
    let total = 0;
    try {
      for (let i = 0; i < 40; i++) {
        const r = await api('reExtractFailed');
        if (!r.success) { notify?.(r.error || 'Repair failed', 'error'); break; }
        total += r.done;
        setProgress(`${total} repaired…`);
        if (r.remaining <= 0) break;
        if (r.done === 0) {
          const err = (r.results || []).find((x) => x.error);
          notify?.(`Stopped — ${err ? err.error : 'no progress'}. ${r.remaining} still to repair (top up API credits, then run again).`, 'error');
          break;
        }
      }
    } finally {
      setBusy(false); setProgress('');
      if (total > 0) notify?.(`${total} case(s) re-read from their PDFs`, 'success');
      onDone();
    }
  };

  return (
    <button className="btn btn-secondary btn-sm" disabled={busy} onClick={run}
      title="Re-run the AI read over cases whose PDF didn't parse">
      <Wrench size={14} /> {busy ? (progress || 'Repairing…') : `Repair extraction (${broken})`}
    </button>
  );
}

export default function CustomerCasesPage() {
  const { addNotification, currentUser } = useApp();
  // Paul and Jayda are admins. A team leader (Billy on Darlinghurst) gets the same page
  // scoped to their own area by the backend, but never makes the fault call and never
  // deletes a case — so the controls that do those things aren't drawn for them.
  const isAdmin = currentUser?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  // The office's own verdict, separate from the case's status: 'all' | 'yes' | 'no' | 'none'.
  // A case is only At fault / Not at fault once someone has recorded the call, so "none" is
  // a real answer too — it's every case the verdict was never written on.
  const [faultFilter, setFaultFilter] = useState('all');
  // ?q= deep link from the global search palette pre-fills the search box.
  const [searchParams] = useSearchParams();
  const qParam = searchParams.get('q') || '';
  const [q, setQ] = useState(qParam);
  useEffect(() => { if (qParam) setQ(qParam); }, [qParam]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [drawer, setDrawer] = useState(null); // { mode:'add'|'view', id? , fields?, file? }
  const [pageDrag, setPageDrag] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [view, setView] = useState('cases'); // 'cases' | 'requests' | 'stats'
  const [reqWaiting, setReqWaiting] = useState(0);   // standing requests nobody has acknowledged

  useEffect(() => { api('driversList').then((r) => { if (r && r.success) setDrivers((r.drivers || []).slice().sort((a, b) => a.login.localeCompare(b.login))); }).catch(() => {}); }, []);

  // Push alerts for the office. Start the worker, quietly re-register this device if its
  // subscription has lapsed, and keep the HP Engine tile's number in step with what is
  // actually waiting — triage plus the cases where a driver has replied and the ball is
  // back here. alertCount is already scope-aware, so a team leader sees their own slice.
  // While the app is CLOSED the service worker sets this from each push, because iOS never
  // wakes a web app to look for itself.
  useEffect(() => {
    registerSW();
    syncAlerts('engine');
  }, []);
  useEffect(() => {
    let alive = true;
    api('alertCount')
      .then((r) => {
        if (!alive || !r || !r.success) return;
        setTileBadge((r.count || 0) + (r.responded || 0));
        // Kept off the tile badge on purpose: the phone number means "cases waiting on the
        // office", and a driver who hasn't read a request is not that. It shows on the tab.
        setReqWaiting(r.requestsWaiting || 0);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [rows]);

  const onPageDrop = (e) => {
    e.preventDefault();
    setPageDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    if (!isAllowedFile(f)) { addNotification?.('Please drop a PDF or image', 'error'); return; }
    setDrawer({ mode: 'add', fields: emptyFields(), file: f });
  };

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api('listCases', { status: statusFilter });
    if (res.success) setRows(res.cases || []);
    else addNotification?.(res.error || 'Failed to load', 'error');
    setLoading(false);
  }, [statusFilter, addNotification]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    // Received-date window — same date the "Received" column shows (AP's case date, else ingest).
    const receivedAt = (r) => { const d = new Date(r.created_date || r.created_at); return isNaN(d) ? 0 : d.getTime(); };
    const dayOf = (r) => { const t = receivedAt(r); return t ? new Date(t).toLocaleDateString('en-CA') : ''; };
    const list = rows.filter((r) => {
      if (faultFilter !== 'all') {
        const f = (r.fault || '').toString().trim().toLowerCase();
        if (faultFilter === 'none' ? !!f : f !== faultFilter) return false;
      }
      if (dateFrom || dateTo) {
        const day = dayOf(r);
        if (dateFrom && (!day || day < dateFrom)) return false;
        if (dateTo && (!day || day > dateTo)) return false;
      }
      if (!needle) return true;
      return `${r.case_number} ${r.enquiry_sub_type} ${r.customer_name} ${r.customer_address} ${r.driver_login || ''} ${r.ap_driver_login || ''}`.toLowerCase().includes(needle);
    });
    // Open and All are worklists, so they keep the backend's due-first order — the case due
    // soonest (or already overdue) is the one to pick up next, and burying it under this
    // morning's arrivals would hide it. Both carry live cases, so both need that. The rest
    // are finished-case registers, read newest-first.
    //
    // Sorted here rather than left to the backend because the list reads by the "Received"
    // date — AP's case date when it's known, which is not the row's ingest time — so a case
    // emailed days late would otherwise land at the top.
    if (statusFilter === 'open' || statusFilter === 'all' || statusFilter === 'reply_due') return list;
    return list.sort((a, b) => (receivedAt(b) - receivedAt(a)) || (new Date(b.created_at) - new Date(a.created_at)));
  }, [rows, q, dateFrom, dateTo, statusFilter, faultFilter]);

  const needTriage = rows.filter((r) => r.status === 'open' && r.match_status !== 'matched').length;
  // Resolved but never classified — the case is still the office's until the fault call is
  // made, so it stays in the Open list and is counted here rather than quietly disappearing.
  const needFault = rows.filter((r) => r.awaiting_fault).length;
  // Open cases where the driver spoke last — the office owes a reply.
  const needReply = rows.filter((r) => r.reply_due).length;
  // A repeat at an address already on the Pre-Card register — the one that must not be
  // triaged as if it were the first complaint about that door.
  const needPrecard = rows.filter((r) => r.precard).length;

  return (
    <div
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setPageDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setPageDrag(false); }}
      onDrop={onPageDrop}
      style={{ position: 'relative', minHeight: '60vh' }}
    >
      {pageDrag && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ border: '2px dashed var(--hp-gold)', borderRadius: 12, padding: '40px 60px', textAlign: 'center', color: '#fff', background: 'var(--hp-surface)' }}>
            <UploadCloud size={40} style={{ color: 'var(--hp-gold)' }} />
            <div style={{ marginTop: 10, fontWeight: 700 }}>Drop the AP case PDF to start a new case</div>
            <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>It'll be read and the questions drafted for you to check</div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Headphones size={22} style={{ color: 'var(--hp-gold)' }} /> Customer Cases</h2>
          <p>{filtered.length} of {rows.length} shown · Australia Post customer enquiries
            {needTriage > 0 && <span style={{ color: '#d97706', fontWeight: 700 }}> · {needTriage} awaiting triage</span>}
            {needFault > 0 && <span style={{ color: '#d97706', fontWeight: 700 }}> · {needFault} awaiting the fault call</span>}
            {needReply > 0 && <span style={{ color: '#b45309', fontWeight: 700 }}> · {needReply} waiting on your reply</span>}
            {needPrecard > 0 && <span style={{ color: PRECARD_RED, fontWeight: 700 }}> · {needPrecard} at a Pre-Card delivery point</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', border: '1px solid var(--hp-border)', borderRadius: 8, overflow: 'hidden' }}>
            {[['cases', 'Cases'], ['requests', 'Requests'], ['stats', 'Stats']].map(([k, lbl]) => (
              <button key={k} onClick={() => setView(k)} style={{ padding: '7px 14px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '.82rem', background: view === k ? 'var(--hp-gold)' : 'transparent', color: view === k ? 'var(--hp-black)' : 'var(--hp-dim)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {lbl}
                {/* A request nobody has read is the whole failure this feature exists to stop,
                    so the count sits on the tab rather than waiting to be found inside it. */}
                {k === 'requests' && reqWaiting > 0 && (
                  <span style={{ fontSize: '.68rem', fontWeight: 800, background: '#dc2626', color: '#fff', borderRadius: 9, padding: '1px 6px' }}>{reqWaiting}</span>
                )}
              </button>
            ))}
          </div>
          {view === 'cases' && <>
            {isAdmin && <RepairExtractionButton rows={rows} onDone={load} notify={addNotification} />}
            <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
            <button className="btn btn-primary" onClick={() => setDrawer({ mode: 'add', fields: emptyFields() })}><Plus size={14} /> Add manually</button>
          </>}
        </div>
      </div>

      {view === 'stats' ? (
        <div className="page-body"><CaseStatsView notify={addNotification} /></div>
      ) : view === 'requests' ? (
        <DeliveryRequestsView notify={addNotification} onCount={setReqWaiting}
          onOpenCase={(id) => setDrawer({ mode: 'view', id })} />
      ) : (
      <div className="page-body">
        <div style={{ maxWidth: 640 }}><PushAlertsCard app="engine" /></div>
        <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 240px' }}>
            <label style={labelStyle}>Search case / customer / driver / address</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
              <input className="input" style={{ ...inputStyle, paddingLeft: 30 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. 73297278, Ross St, SHARLANDS2…" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select className="input" style={{ ...inputStyle, width: 140 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="open">Open</option>
              <option value="awaiting_fault">Awaiting fault call</option>
              <option value="reply_due">Waiting on your reply</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
              <option value="cancelled">Cancelled</option>
              <option value="all">All</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Fault</label>
            <select className="input" style={{ ...inputStyle, width: 150 }} value={faultFilter} onChange={(e) => setFaultFilter(e.target.value)}
              title="The office's At fault / Not at fault call — recorded on a case once it's been answered and resolved">
              <option value="all">Any</option>
              <option value="no">Not at fault</option>
              <option value="yes">At fault</option>
              <option value="none">No call recorded</option>
            </select>
          </div>
          {/* The fault call is made on finished cases, so asking for one while the list is
              cut to the open desk usually shows nothing. Say why, and offer the fix, rather
              than quietly widening the status filter under them. */}
          {faultFilter !== 'all' && statusFilter === 'open' && (
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: 2 }} onClick={() => setStatusFilter('all')}
              title="The fault call is recorded once a case is answered and resolved, so it's rarely on the open desk">
              Searching open cases only — show all statuses
            </button>
          )}
          <div>
            <label style={labelStyle}>Received from</label>
            <input type="date" className="input" style={{ ...inputStyle, width: 150 }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" className="input" style={{ ...inputStyle, width: 150 }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          {(dateFrom || dateTo) && (
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: 2 }} onClick={() => { setDateFrom(''); setDateTo(''); }} title="Clear the date range">
              <X size={13} /> Clear dates
            </button>
          )}
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}>
              {rows.length === 0 ? <>No customer cases. They arrive by email automatically, or click <strong>Add manually</strong>.</> : 'None match your search.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
                <thead>
                  <tr>
                    <th style={th}>Due</th>
                    <th style={th}>Case</th>
                    <th style={th}>Received</th>
                    <th style={th}>About</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Driver</th>
                    <th style={th}>Status</th>
                    <th style={{ ...th, textAlign: 'right' }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const c = cat(r.case_category);
                    return (
                      <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setDrawer({ mode: 'view', id: r.id })}>
                        <td style={td}>{r.status === 'open' ? <DueChip due_at={r.due_at} /> : <span style={{ color: 'var(--hp-dim)', fontSize: '.72rem' }}>—</span>}</td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                          {r.case_number || '—'}
                          {r.attachment_path && <FileText size={12} style={{ marginLeft: 6, color: 'var(--hp-dim)' }} title="Has PDF" />}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontSize: '.8rem' }} title={apOpenedNote(r) || 'The date on the investigation form — when AP sent us the case'}>
                          {fmtReceived(r.created_date || r.created_at)}
                          {apOpenedNote(r) && <div style={{ fontSize: '.7rem', color: '#d97706' }}>AP opened {fmtReceived(apOpened(r))}</div>}
                        </td>
                        <td style={td}><Badge text={c.label} color={c.color} /></td>
                        <td style={{ ...td, maxWidth: 240 }}>
                          <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.customer_name || (r.customer_role === 'sender' ? 'Sender' : 'Receiver')}</div>
                          <div style={{ fontSize: '.74rem', color: r.precard ? PRECARD_RED : 'var(--hp-dim)', fontWeight: r.precard ? 700 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={r.precard ? `Pre-Card delivery point — ${r.precard.why}. ${r.precard.driver_note || ''}` : undefined}>
                            {r.precard && <ShieldAlert size={11} style={{ verticalAlign: '-1px', marginRight: 3 }} />}
                            {r.customer_address || '—'}
                          </div>
                        </td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: '.8rem' }}>
                          {r.match_status === 'matched'
                            ? <span>{r.driver_login}{r.responseCount > 0 && <span style={{ marginLeft: 6, color: '#16a34a', fontSize: '.68rem' }} title={`${r.driver_login} has written ${r.responseCount} time${r.responseCount > 1 ? 's' : ''} on this case`}>✓ {r.responseCount}</span>}
                                {/* Someone other than the office spoke last, so the case is
                                    waiting on us — but SAY WHO. This sits under the driver's
                                    login, and an unattributed "replied" read as the driver
                                    even when it was Australia Post's own mail. */}
                                {r.reply_due && (
                                  <div style={{ fontSize: '.68rem', color: '#b45309', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}
                                    title={r.last_reply_via === 'ap'
                                      ? `Australia Post wrote${r.last_reply_at ? ` — ${fmtDateTime(r.last_reply_at)}` : ''}. ${r.driver_login} has not replied. Nobody in the office has answered yet.`
                                      : `The driver replied${r.last_reply_at ? ` — ${fmtDateTime(r.last_reply_at)}` : ''}. Nobody in the office has answered yet.`}>
                                    <MessageSquare size={11} /> {r.last_reply_via === 'ap' ? 'AP wrote — your turn' : 'replied — your turn'}
                                  </div>
                                )}
                                {/* The driver answered, but the office has since put another
                                    question to them — the wait is theirs, not ours. */}
                                {r.awaiting_driver && (
                                  <div style={{ fontSize: '.68rem', color: '#2563eb', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}
                                    title={`The office replied and asked ${r.driver_login} a further question${r.asked_at ? ` — ${fmtDateTime(r.asked_at)}` : ''}. Waiting on their answer.`}>
                                    <MessageSquare size={11} /> asked again — awaiting reply
                                  </div>
                                )}
                              </span>
                            : <span style={{ color: '#d97706', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <AlertTriangle size={12} />{r.match_status === 'suggested' ? `${suggestion(r).suggested_driver || r.ap_driver_login}?` : r.ap_driver_login ? `${r.ap_driver_login}?` : 'assign'}
                              </span>}
                          {r.assist_status === 'open' && r.assist_login && (
                            <div style={{ fontSize: '.68rem', color: '#2563eb', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}
                              title={`Recovery sent to ${r.assist_login}${r.assist_pushed_at ? ` — ${fmtDateTime(r.assist_pushed_at)}` : ''}`}><UserPlus size={11} /> {r.assist_login}</div>
                          )}
                        </td>
                        <td style={td}>
                          <Badge text={r.status} color={STATUS_COLORS[r.status] || 'var(--hp-dim)'} />
                          {r.awaiting_fault && (
                            <div style={{ fontSize: '.68rem', color: '#d97706', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3, marginTop: 3 }}
                              title="Answered and resolved, but nobody has recorded At fault / Not at fault yet — it stays in triage until they do">
                              <AlertTriangle size={11} /> fault call due
                            </div>
                          )}
                          {/* The verdict itself, so a list filtered to At fault / Not at
                              fault reads as one — the status alone says "resolved" either way. */}
                          {(r.fault === 'yes' || r.fault === 'no') && (
                            <div style={{ fontSize: '.68rem', fontWeight: 700, marginTop: 3, color: r.fault === 'yes' ? '#b91c1c' : '#16a34a' }}
                              title={r.fault_reason ? `${r.fault === 'yes' ? 'At fault' : 'Not at fault'} — ${r.fault_reason}` : undefined}>
                              {r.fault === 'yes' ? 'At fault' : 'Not at fault'}
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}><ExternalLink size={14} style={{ color: 'var(--hp-dim)' }} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {drawer && (
        <CaseDrawer
          drawer={drawer}
          onClose={() => { setDrawer(null); load(); }}
          onSaved={() => { setDrawer(null); load(); }}
          notify={addNotification}
          drivers={drivers}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}

// The top-of-case answer to "what does the driver have?": the note, the questions, and
// whether they've answered — each with its time. Deliberately loud when the answer is
// "nothing": a case in triage that nobody has pushed looks identical, on paper, to one
// that's been sitting in front of the driver for a week.
// ══════════════════════════════════════════════════════════════════════════════
// Pre-Card delivery points
// ══════════════════════════════════════════════════════════════════════════════
// A Pre-Card point is an address we have decided is not safe to deliver to the door:
// the driver issues a card on the spot and the parcel goes to the Post Office. The
// register is the safety hazard register on /safety — one list, so a dangerous address
// cannot be written down in one place and missed in the other — narrowed to the entries
// whose standing action is "do not attempt".
//
// This banner is the point of the whole thing. A case arriving for an address already on
// the register is not a fresh complaint to work out from scratch; it is a point we have
// already ruled on, coming back. That has to be the first thing read on the case, above
// Australia Post's summary, or the office answers the second complaint as if it were the
// first. Paul, 28 Aug 2026, off case 73616057 at 23 Pyrmont Bridge Rd.

const PRECARD_RED = '#dc2626';

function PrecardBanner({ precard, onClear, isAdmin, busy }) {
  if (!precard) return null;
  const partial = precard.level === 'partial';
  const col = partial ? '#d97706' : PRECARD_RED;
  return (
    <div style={{
      border: `1px solid ${col}`, borderLeft: `5px solid ${col}`, borderRadius: 8, padding: 12,
      background: partial ? 'rgba(217,119,6,.07)' : 'rgba(220,38,38,.08)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <ShieldAlert size={16} style={{ color: col }} />
        <span style={{ fontWeight: 800, fontSize: '.86rem', color: col, letterSpacing: '.03em' }}>
          {partial ? 'POSSIBLE PRE-CARD DELIVERY POINT' : 'PRE-CARD DELIVERY POINT'}
        </span>
        {precard.since && <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>on the register since {fmtDate(precard.since)}</span>}
        {isAdmin && onClear && (
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} disabled={busy} onClick={onClear}
            title="Take this address back off the register — drivers stop seeing the warning">
            Clear from register
          </button>
        )}
      </div>
      <div style={{ fontSize: '.86rem', marginTop: 7, fontWeight: 700 }}>{precard.address}</div>
      <div style={{ fontSize: '.79rem', color: 'var(--hp-dim)', marginTop: 3 }}>
        Matched because {precard.why}.
        {partial && ' The case address is not written out fully — check it is the same door before you rely on this.'}
      </div>
      {precard.driver_note && (
        <div style={{ fontSize: '.84rem', marginTop: 8, padding: '7px 10px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6, lineHeight: 1.5 }}>
          <strong>Standing instruction:</strong> <span style={{ whiteSpace: 'pre-line' }}>{precard.driver_note}</span>
        </div>
      )}
      <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginTop: 8, lineHeight: 1.5 }}>
        Do not answer this as a first complaint. The parcel is carded to the Post Office by standing
        instruction, and the driver whose route covers this street has already been told so.
        {' '}<Link to="/pre-card" style={{ color: 'var(--hp-gold)', fontWeight: 700 }}>Open the Pre-Card register →</Link>
      </div>
    </div>
  );
}

// Put the address on the register from the case that revealed it — the office knows while
// they are reading the complaint, not later on another page.
function AddPrecardBox({ caseId, address, onAdded, notify }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [action, setAction] = useState('card_to_po');
  const [wholeBuilding, setWholeBuilding] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!address) return null;

  // "The whole building" strips the unit off the front, so any door in the block matches.
  // That is the setting for a dangerous entrance, lift, foyer or stairwell rather than one
  // flat — and it is a real decision, so it is a tick the office makes, never a guess.
  const registerAddress = wholeBuilding
    ? address.replace(/^\s*\d{1,5}[A-Z]?\s*\/\s*/, '').replace(/^\s*(U|UNIT|APT|APARTMENT|L|LVL|LEVEL|SHOP|SUITE|STE|FLAT|RM|ROOM|TOWER)\s*\.?\s*\d{1,4}[A-Z]?\s*,?\s*/i, '')
    : address;

  const save = async () => {
    if (!reason.trim()) { notify?.('Say what makes it unsafe — the driver reads it at the gate', 'error'); return; }
    setBusy(true);
    const r = await api('addPrecardPoint', { id: caseId, address: registerAddress, reason: reason.trim(), pointAction: action });
    setBusy(false);
    if (r.success) { notify?.('Added to the Pre-Card register — the driver on this street sees it now', 'success'); setOpen(false); setReason(''); onAdded?.(); }
    else notify?.(r.error || 'Failed to add', 'error');
  };

  if (!open) {
    return (
      <button className="btn btn-ghost btn-sm" style={{ color: PRECARD_RED }} onClick={() => setOpen(true)}
        title="Record this address as a Pre-Card delivery point — the driver cards it and it goes to the Post Office">
        <ShieldAlert size={13} /> Add to Pre-Card register
      </button>
    );
  }

  return (
    <div style={{ border: `1px solid ${PRECARD_RED}`, borderRadius: 8, padding: 12, marginTop: 8 }}>
      <div style={{ fontWeight: 800, fontSize: '.82rem', color: PRECARD_RED, display: 'flex', alignItems: 'center', gap: 6 }}>
        <ShieldAlert size={14} /> Add to the Pre-Card register
      </div>
      <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: 5, lineHeight: 1.5 }}>
        This goes on the safety hazard register and is forced in front of the driver whose route covers
        the street — they have to tap "I have read this", and the tap is stamped against them.
      </div>

      <label style={{ ...labelStyle, marginTop: 10 }}>Address going on the register</label>
      <div style={{ fontSize: '.85rem', fontWeight: 700, padding: '7px 10px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6 }}>{registerAddress}</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.8rem', marginTop: 7, cursor: 'pointer' }}>
        <input type="checkbox" checked={wholeBuilding} onChange={(e) => setWholeBuilding(e.target.checked)} />
        The whole building, not just this unit
        <span style={{ color: 'var(--hp-dim)', fontSize: '.74rem' }}>— use this if the entrance, lift or foyer is the problem</span>
      </label>

      <label style={{ ...labelStyle, marginTop: 10 }}>What the driver must do</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[['card_to_po', 'Card to the PO', 'Do not attempt delivery to the door'],
          ['do_not_enter', 'CAN NOT ENTER', 'There is no way into the building at all — locked, no working intercom']].map(([k, lbl, hint]) => (
          <button key={k} title={hint} className={`btn btn-sm ${action === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setAction(k)}>{lbl}</button>
        ))}
      </div>

      <label style={{ ...labelStyle, marginTop: 10 }}>Why — the driver reads this at the gate, and it goes in the letter to Australia Post</label>
      <textarea className="input" style={{ ...inputStyle, minHeight: 78, lineHeight: 1.5, resize: 'vertical' }} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder={'One fact per line, e.g.\nThe building is locked.\nThe intercom does not work.'} />
      <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 4 }}>
        Facts only — what the driver must DO is already on the action you picked above.
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => setOpen(false)}>Cancel</button>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Add to register'}</button>
      </div>
    </div>
  );
}

// ── Driver Portal View ───────────────────────────────────────────────────────
// The office's own eyes on the driver's phone. Everything above this is the office's
// account of what was pushed — chips, timestamps, switches. This is the thing itself:
// the driver's real card (components/CustomerCasePortalCard, the component the portal
// renders) over the data the portal is served (driverPreview → _shared/case-driver-view,
// the portal's own builder). A preview drawn a second time office-side would drift the
// first time either end changed, and a preview you can't trust is worse than none.
//
// Read-only: `preview` leaves every control exactly where the driver sees it and lets
// none of them post. Nothing here answers a case in a driver's name.
const PREVIEW_STATES = {
  live:      { color: '#15803d', bg: 'rgba(22,163,74,0.12)',  border: 'rgba(22,163,74,0.45)' },
  history:   { color: '#475569', bg: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.40)' },
  unmatched: { color: '#b45309', bg: 'rgba(245,158,11,0.14)', border: 'rgba(245,158,11,0.5)' },
  nodriver:  { color: '#b91c1c', bg: 'rgba(220,38,38,0.10)',  border: 'rgba(220,38,38,0.45)' },
};
const previewTab = (active) => ({
  flex: 1, padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: '.78rem',
  border: `1px solid ${active ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
  background: active ? 'rgba(201,168,76,0.15)' : 'transparent', color: 'var(--hp-text)',
});

function DriverPortalPreview({ caseId, onClose }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setRes(null); setErr('');
    api('driverPreview', { id: caseId })
      .then((r) => { if (!alive) return; if (r && r.success) setRes(r); else setErr((r && r.error) || 'Could not load the driver view'); })
      .catch(() => { if (alive) setErr('Network error — try again'); });
    return () => { alive = false; };
  }, [caseId]);

  // Which phone we are looking at. A case with a live recovery is on TWO portals, and they
  // do not show the same thing — the helper gets the search task and the audit snapshot,
  // never the questionnaire or the fault. Stacking both cards made the second one something
  // you scrolled past; a switch makes it something you check (Paul, 1 Sep 2026).
  const [screen, setScreen] = useState('driver');   // 'driver' | 'assist'
  const showAssist = !!(res && res.assistCase) && screen === 'assist';
  const verdict = res ? previewVerdict(res.visibility, res.driver) : null;
  const tone = verdict ? PREVIEW_STATES[verdict.state] : null;
  const who = showAssist ? res.assistDriver : (res && res.driver);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '3vh 10px', overflowY: 'auto' }}
      onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="card" style={{ width: 'min(460px, 100%)', maxWidth: '100%', padding: 0, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid var(--hp-border)' }}>
          <Smartphone size={17} style={{ color: 'var(--hp-gold)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '.95rem' }}>Driver Portal View</div>
            <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)' }}>
              {res ? (who ? `What ${who} sees on their phone` : 'What the driver would see on their phone') : 'Loading the driver\u2019s screen\u2026'}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close" title="Close"
            style={{ minWidth: 38, minHeight: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><X size={17} /></button>
        </div>

        {err && <div style={{ padding: 16, color: 'var(--hp-danger)', fontSize: '.85rem' }}>{err}</div>}
        {!res && !err && <div style={{ padding: 16, color: 'var(--hp-dim)', fontSize: '.85rem' }}><Loader size={14} /> Loading&hellip;</div>}

        {res && (
          <>
            {res.assistCase && (
              <div style={{ display: 'flex', gap: 6, padding: '12px 12px 0' }}>
                <button type="button" onClick={() => setScreen('driver')} style={previewTab(!showAssist)}>{res.driver || 'Driver'}</button>
                <button type="button" onClick={() => setScreen('assist')} style={previewTab(showAssist)}>{res.assistDriver} · recovery</button>
              </div>
            )}
            {showAssist ? (
              <div style={{ margin: 12, padding: '9px 11px', borderRadius: 8, border: '1px solid rgba(37,99,235,0.45)', borderLeft: '4px solid #1d4ed8', background: 'rgba(37,99,235,0.10)', fontSize: '.8rem', color: '#1d4ed8', fontWeight: 700 }}>
                On {res.assistDriver}&rsquo;s portal now — the recovery only. They never see the questionnaire, the fault call or the answer {res.driver} owes: this case stays {res.driver}&rsquo;s.
              </div>
            ) : (
              <div style={{ margin: 12, padding: '9px 11px', borderRadius: 8, border: `1px solid ${tone.border}`, borderLeft: `4px solid ${tone.color}`, background: tone.bg, fontSize: '.8rem', color: tone.color, fontWeight: 700 }}>
                {verdict.text}
              </div>
            )}
            <div style={{ padding: '0 12px 10px', fontSize: '.76rem', color: 'var(--hp-dim)' }}>
              Read-only. The buttons are the driver&rsquo;s — tapping one here sends nothing.
            </div>
            {/* The driver's screen, tinted apart from the office panels around it. It
                follows YOUR theme (global.css) — your driver's phone may be set to the
                other one; nothing else about the card changes with it. */}
            <div className="portal-preview-frame" style={{ padding: 12, borderTop: '1px solid var(--hp-border)' }}>
              <div style={{ fontSize: '.68rem', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 8 }}>
                Customer Service{who ? ` \u00b7 ${who}` : ''}
              </div>
              {/* Standing requests live OUTSIDE the case on the driver's phone — on their
                  shift screen until acknowledged, then kept under Customer Service →
                  Requests. Shown here because this window is where the office comes to ask
                  "what is this driver actually looking at?", and the answer includes these. */}
              {!showAssist && (res.requests || []).length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: '.68rem', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 8 }}>
                    On their shift screen &amp; kept under Requests
                  </div>
                  {res.requests.map((r) => <DeliveryRequestCard key={r.id} r={r} preview />)}
                </div>
              )}
              {showAssist
                ? <AssistCaseCard c={res.assistCase} preview />
                : res.case
                  ? <CustomerCaseCard c={res.case} preview startOpen />
                  : <div style={{ fontSize: '.85rem', color: 'var(--hp-dim)' }}>Nothing on this case reaches a driver&rsquo;s portal.</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DriverPushBanner({ row, matched, onPreview, photoCount = 0, seen = {} }) {
  const note = (row.driver_note || '').toString().trim();
  const qs = Array.isArray(row.questionnaire) ? row.questionnaire : [];
  const answered = !!row.questionnaire_answers;
  // Answered once isn't answered in full: a question left blank or added afterwards is back on
  // the driver's portal as a follow-up round. Photo questions are left out — the portal hides
  // those when there's no photo, so they can't be owed.
  const owed = answered ? pendingQuestions({ ...row, images: [] }) : [];
  const open = row.status === 'open';
  const live = matched && open;                       // on the driver's portal right now
  const nothing = !note && !qs.length;
  const who = row.driver_login || 'the driver';

  // WHEN the case went to the driver — released_at is the case's own record of the push,
  // and it outlives the case being closed. Closing a case takes it off the driver's to-do
  // list; it does not un-send it. Saying "not on their portal / nothing pushed" about a
  // case the driver was sent, answered and had resolved reads as "nobody ever told them",
  // which is the opposite of what happened (Paul, 27 Aug 2026). So a finished case reports
  // the push and its time, in a settled grey — red is kept for never-pushed.
  const pushedAt = row.released_at || (row.scan_snapshot && row.scan_snapshot.pushedAt) || null;
  const everPushed = !!(matched && (pushedAt || answered || (!open && (note || qs.length))));
  const done = !live && everPushed;                   // pushed, and no longer being asked

  const helping = row.assist_login && row.assist_status === 'open';
  const tone = live ? ((nothing && !helping) ? 'unpushed' : 'pushed') : done ? 'history' : 'unsaved';
  const t = PUSH_TONES[tone];
  const headline = !matched
    ? 'Not on any driver’s portal — no driver confirmed'
    : !open
      ? (everPushed
          ? `Case ${row.status} — pushed to ${who}${pushedAt ? ` ${fmtDateTime(pushedAt)}` : ''}, now in their history not their to-do`
          : `Case ${row.status} — nothing was ever pushed to ${who}`)
      : nothing
        ? (helping ? `${row.assist_login} is recovering this — nothing pushed to ${who}` : `Nothing pushed to ${who} yet`)
        : `Pushed to ${who}${helping ? ` · ${row.assist_login} recovering` : ''}`;

  return (
    <div style={{ border: `1px solid ${t.border}`, borderLeft: `4px solid ${t.color}`, borderRadius: 8, padding: '10px 12px', background: t.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 800, fontSize: '.9rem', color: t.color, textTransform: 'uppercase', letterSpacing: '.03em', flex: '1 1 220px', minWidth: 0 }}>
          {tone === 'pushed' ? <CheckCircle2 size={15} /> : tone === 'history' ? <Check size={15} /> : <AlertTriangle size={15} />} {headline}
        </div>
        {/* The chips below say what was pushed. This shows it — the driver's own card,
            on the driver's own screen, read-only. */}
        <button className="btn btn-secondary btn-sm" onClick={onPreview} style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          title="Open the case exactly as it renders on the driver's phone. Read-only — nothing is sent.">
          <Smartphone size={14} /> Driver Portal View
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {live
          ? <PushState tone="pushed" label="On their portal" when={pushedAt}
              title={`The case itself has been in ${who}'s portal since this time.`} />
          : done
            ? <PushState tone="history" icon={Check} label="Was on their portal" when={pushedAt}
                title={`Pushed to ${who}${pushedAt ? ` on ${fmtDateTime(pushedAt)}` : ''}. The case is ${row.status}, so it now sits in their history rather than their to-do — it was sent.`} />
            : <PushState tone="unpushed" label="Not on their portal" title={matched ? 'This case has never been released to the driver’s portal.' : 'Confirm a driver and the case goes to their portal.'} />}
        {/* "On their portal" says it was sent; this says whether they have looked. Recorded
            from 24 Sep 2026 — an older case with no stamp may still have been opened. */}
        {everPushed && row.driver_login && (() => {
          const s = seen[(row.driver_login || '').toUpperCase()];
          return s
            ? <PushState tone={live ? 'pushed' : 'history'} icon={Eye} label="Opened" when={s.last_at}
                title={`${who} last had this case open ${fmtDateTime(s.last_at)} — first ${fmtDateTime(s.first_at)}, ${s.count} time${s.count === 1 ? '' : 's'}.`} />
            : <PushState tone={live ? 'unsaved' : 'history'} icon={EyeOff} label="Not opened"
                title={`${who} hasn't opened this case on their portal (recorded since 24 Sep 2026).`} />;
        })()}
        {note
          ? <PushState tone="pushed" label="Note" when={row.driver_note_at} title={note} />
          : <PushState tone={done ? 'history' : 'unpushed'} icon={done ? X : undefined} label="No note" title={done ? 'No note was written for the driver on this case.' : 'The driver has no note on this case.'} />}
        {qs.length
          ? <PushState tone="pushed" label={`${qs.length} question${qs.length > 1 ? 's' : ''}`} when={row.questionnaire_at} title={qs.map((q) => q.prompt).join(' · ')} />
          : <PushState tone={done ? 'history' : 'unpushed'} icon={done ? X : undefined} label="No questions" title={done ? 'The driver was never asked anything on this case.' : 'Nothing on this case is waiting for an answer.'} />}
        {/* A photo the office put on their card is a push like any other — it belongs in the
            same row that answers "has this driver been told anything?". */}
        {photoCount > 0 && <PushState tone="pushed" icon={Camera} label={`${photoCount} photo${photoCount > 1 ? 's' : ''} sent`} title="Images the office has put on the driver's card." />}
        {qs.length > 0 && (answered && owed.length && open
          ? <PushState tone="unsaved" label={`${owed.length} still to answer`} title={`Back on ${who}'s portal as a follow-up: ${owed.map((q) => q.prompt).join(' · ')}`} />
          : answered
          ? <PushState tone="pushed" label="Answered" title="The driver has answered the questionnaire — their answers are below." />
          : done
            ? <PushState tone="history" icon={X} label="Never answered" title={`${who} never answered before the case was ${row.status}.`} />
            : <PushState tone="unsaved" label="Awaiting answer" title={`${who} has not answered yet.`} />)}
        {/* A second driver doing the legwork is a push to a DIFFERENT portal, and the case
            reads wrong without it — "nothing pushed" while KIMJ8 is out looking. */}
        {row.assist_login && row.assist_status === 'open' && (
          <PushState tone="assist" icon={UserPlus} label={`Recovery → ${row.assist_login}`} when={row.assist_pushed_at}
            title={row.assist_task || `${row.assist_login} has been asked to recover this parcel.`} />
        )}
        {row.assist_login && row.assist_status === 'done' && (
          <PushState tone="pushed" icon={UserPlus} label={`${row.assist_login} reported back`} when={row.assist_done_at}
            title={`${row.assist_login} did the recovery search and reported back — their answer is with the responses below.`} />
        )}
      </div>
    </div>
  );
}

// "Has the driver actually been told, and when?" — the one question an admin opening a
// case has to be able to answer at a glance. Saving a note or a questionnaire used to leave
// only a quiet grey line, so a case pushed an hour ago read the same as one nobody had ever
// pushed (Paul, 24 Aug 2026). This says it in bold, in the colour of the answer: green with
// the timestamp when the driver has it, red when they don't, amber when the office has typed
// something it hasn't pushed yet.
const PUSH_TONES = {
  pushed:  { color: '#15803d', bg: 'rgba(22,163,74,0.12)',  border: 'rgba(22,163,74,0.45)' },
  unpushed:{ color: '#b91c1c', bg: 'rgba(220,38,38,0.10)',  border: 'rgba(220,38,38,0.45)' },
  unsaved: { color: '#b45309', bg: 'rgba(245,158,11,0.14)', border: 'rgba(245,158,11,0.5)' },
  // A recovery is neither "the office has done its bit" nor "nobody has been told" — it's a
  // second driver carrying the legwork, and it reads as its own thing in the case's own blue.
  assist:  { color: '#1d4ed8', bg: 'rgba(37,99,235,0.10)',   border: 'rgba(37,99,235,0.45)' },
  // Finished business: it happened, it isn't outstanding, and it must not shout. A closed
  // case's chips are a record, not a to-do list — red here would accuse the office of never
  // having pushed a case the driver actually answered.
  history: { color: '#475569', bg: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.40)' },
};
function PushState({ tone, label, when, title, icon }) {
  const t = PUSH_TONES[tone] || PUSH_TONES.unpushed;
  const Icon = icon || (tone === 'pushed' ? CheckCircle2 : AlertTriangle);
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.78rem', fontWeight: 800,
      letterSpacing: '.04em', textTransform: 'uppercase', color: t.color, background: t.bg,
      border: `1px solid ${t.border}`, borderRadius: 6, padding: '4px 9px',
    }}>
      <Icon size={13} />
      {label}
      {when && <span style={{ fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}>· {fmtDateTime(when)}</span>}
    </span>
  );
}

// ── The standing-request register ────────────────────────────────────────────
// Every request the office has raised, and the one thing you actually come here to find out:
// has the driver said they have it? Inside the case that raised it is not good enough — a
// month later nobody remembers which case that was, and the whole point of a standing request
// is that it outlives its case (Paul, 1 Sep 2026).
function DeliveryRequestsView({ notify, onCount, onOpenCase }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const res = await api('deliveryRequests', { includeRetired: showRetired });
    if (res.success) setRows(res.requests || []);
    else { setRows([]); notify?.(res.error || 'Could not load the requests', 'error'); }
  }, [showRetired, notify]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (rows && onCount) onCount(rows.filter((r) => r.active !== false && !r.acked_at).length);
  }, [rows, onCount]);

  const retire = async (r) => {
    setBusy(r.id);
    const res = await api('retireDeliveryRequest', { requestId: r.id });
    setBusy('');
    if (res.success) { notify?.(`Taken off ${r.driver_login}'s phone`, 'success'); load(); }
    else notify?.(res.error || 'Failed', 'error');
  };

  if (!rows) return <div className="card" style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>;
  const n = q.trim().toLowerCase();
  const shown = n ? rows.filter((r) => `${r.address} ${r.request} ${r.driver_login} ${r.case_number}`.toLowerCase().includes(n)) : rows;
  const waiting = shown.filter((r) => r.active !== false && !r.acked_at);
  const done = shown.filter((r) => r.active !== false && r.acked_at);
  const retired = shown.filter((r) => r.active === false);

  const card = (r) => (
    <div key={r.id} className="card" style={{ padding: 14, marginBottom: 10, borderLeft: `4px solid ${r.active === false ? 'var(--hp-muted)' : r.acked_at ? '#16a34a' : '#dc2626'}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, fontSize: '.86rem', fontFamily: 'var(--font-mono)' }}>{r.driver_login}</span>
        {/* The request follows the round: when it has changed hands, say who the case named. */}
        {r.raised_on && r.raised_on !== r.driver_login && <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>on the round now · raised on {r.raised_on}</span>}
        {r.area && <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>{r.area}</span>}
        {r.active === false
          ? <span style={{ fontSize: '.74rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--hp-dim)' }}>Retired{r.retired_at ? ` ${fmtDateTime(r.retired_at)}` : ''}</span>
          : r.acked_at
            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.76rem', fontWeight: 800, color: '#15803d' }}><CheckCircle2 size={13} /> Acknowledged {fmtDateTime(r.acked_at)}</span>
            : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.76rem', fontWeight: 800, color: '#b91c1c' }}><AlertTriangle size={13} /> Not read yet</span>}
        <span style={{ marginLeft: 'auto', fontSize: '.74rem', color: 'var(--hp-dim)' }}>
          Raised {fmtDateTime(r.created_at)}{r.created_by ? ` by ${r.created_by}` : ''}
        </span>
        {r.active !== false && (
          <button className="btn btn-ghost btn-sm" disabled={busy === r.id} onClick={() => retire(r)}
            title="Take it off their phone — the record is kept" style={{ color: '#b91c1c' }}><X size={13} /></button>
        )}
      </div>
      <div style={{ fontSize: '.92rem', fontWeight: 700, marginTop: 6, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <MapPin size={14} style={{ color: 'var(--hp-gold)', flexShrink: 0, marginTop: 2 }} /> {r.address}
      </div>
      <div style={{ fontSize: '.88rem', marginTop: 5, whiteSpace: 'pre-wrap' }}>{r.request}</div>
      {(r.photos || []).length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {r.photos.map((src, i) => <a key={i} href={src} target="_blank" rel="noreferrer"><img src={src} alt="" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} /></a>)}
        </div>
      )}
      {r.case_number && (
        <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: 7 }}>
          From case {r.case_number}
          {r.case_id && onOpenCase && <> · <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px' }} onClick={() => onOpenCase(r.case_id)}>open it</button></>}
        </div>
      )}
    </div>
  );

  return (
    <div className="page-body">
      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ ...inputStyle, flex: '1 1 260px' }} value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search an address, a driver, a case number…" />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem', color: 'var(--hp-dim)', cursor: 'pointer' }}>
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Include retired
        </label>
        <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
      </div>

      {!shown.length && (
        <div className="card" style={{ padding: 26, color: 'var(--hp-dim)' }}>
          {rows.length ? 'None match your search.' : 'No standing requests yet. Raise one from a case — the panel is under the questionnaire.'}
        </div>
      )}

      {waiting.length > 0 && (
        <>
          <div style={{ ...labelStyle, color: '#b91c1c' }}>Waiting on the driver ({waiting.length})</div>
          {waiting.map(card)}
        </>
      )}
      {done.length > 0 && (
        <>
          <div style={{ ...labelStyle, marginTop: waiting.length ? 18 : 0 }}>Acknowledged ({done.length})</div>
          {done.map(card)}
        </>
      )}
      {retired.length > 0 && (
        <>
          <div style={{ ...labelStyle, marginTop: 18 }}>Retired ({retired.length})</div>
          {retired.map(card)}
        </>
      )}
    </div>
  );
}

// ── Raise a standing delivery request off a case ─────────────────────────────
// The gap this closes (Paul, 1 Sep 2026): a case carrying "leave it behind the pot plant
// from now on" was pushed to the driver and resolved the same day, so it dropped into his
// RESOLVED list unread. The customer's wish outlives the case by years; the case does not.
//
// A request is not a case message and not a hazard. It goes on the driver's phone as
// something they must actively acknowledge, it stays there afterwards for them to look up
// at the door, and the office keeps the date they said they had it.
// ── Authority to Leave off a case ────────────────────────────────────────────
// AP sometimes closes a case by approving an Authority to Leave — for 74101158 an
// Accessibility & Disability accommodation: the customer cannot get to the Post Office, so
// every parcel (signature items too) goes in the receptacle at the property. One button
// records it on Standing Orders (Customers with a disability / Authority to Leave), files the
// case PDF and every AP email on its paper trail as the evidence, alerts the driver(s) on
// that street and their team leader, and from then on reminds those drivers every morning.
// It is a standing arrangement, so it outlives the case: resolving the case does not end it.
const SO_URL = '/.netlify/functions/standing-orders-sb';
const soAdmin = (action, payload = {}) =>
  fetch(SO_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

function AtlFromCaseBox({ row, address, apText, notify }) {
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const looksAd = /disab|accessib|a\s*&\s*d\b|mobility/i.test(apText || '');
  const [f, setF] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const load = useCallback(async () => {
    try {
      const r = await soAdmin('caseAtl', { caseId: row.id, caseNumber: row.case_number });
      // caseAtl also returns this case's take-special-care notice; that has its own box.
      setOrders(r && r.success ? (r.orders || []).filter((o) => o.kind === 'atl') : []);
    } catch { setOrders([]); }
  }, [row.id, row.case_number]);
  useEffect(() => { load(); }, [load]);

  const start = () => {
    setF({
      customer: row.addressee_name || row.customer_name || '',
      address,
      receptacle: 'the parcel receptacle at the property',
      include_signature: true,
      disability: looksAd,
      ad_details: looksAd
        ? `Australia Post approved an Accessibility & Disability accommodation (case ${row.case_number || ''}): the customer has advised they have mobility limitations and cannot attend the Post Office to collect carded articles. Delivery point assessed; Authority to Leave approved by the Facility Manager, with a parcel receptacle at the property.`
        : '',
      instruction: '',
      alert: true,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!f.address.trim()) { notify?.('Which address is the ATL for?', 'error'); return; }
    setBusy(true);
    const res = await soAdmin('fromCase', {
      caseId: row.id, notify: f.alert,
      fields: {
        kind: 'atl', disability: f.disability, receptacle: f.receptacle, include_signature: f.include_signature,
        ad_details: f.ad_details, customer: f.customer, instruction: f.instruction, from: { name: '', address: f.address },
      },
    });
    setBusy(false);
    if (!res.success) { notify?.(res.error || 'Could not record the ATL', 'error'); if (res.id) load(); return; }
    setOpen(false);
    const how = res.sent && res.sent.results ? ` · alerted ${res.sent.results.map((x) => `${x.login} (${x.how})`).join(', ')}` : '';
    notify?.(`Authority to Leave recorded — ${res.records} record${res.records === 1 ? '' : 's'} filed as evidence${how}`, 'success');
    load();
  };

  const live = (orders || []).filter((o) => o.status === 'active');
  return (
    <div style={{ border: `1px solid ${live.length ? '#16a34a' : 'var(--hp-border)'}`, borderRadius: 8, padding: 12 }}>
      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><PackageCheck size={12} /> Authority to Leave{looksAd ? ' — customer with a disability' : ''}</div>

      {live.map((o) => (
        <div key={o.id} style={{ border: '1px solid rgba(22,163,74,0.45)', background: 'rgba(22,163,74,0.08)', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', fontSize: '.84rem', fontWeight: 700 }}>
            {o.disability && <Accessibility size={13} style={{ color: '#2563eb' }} />}
            On Standing Orders since {fmtDateTime(o.created_at)} — {(o.from && o.from.address || '').split(',')[0]}, leave all in {o.receptacle || 'the receptacle'}{o.include_signature ? ' (signature items included)' : ''}
            <Link to={`/standing-orders?open=${o.id}`} style={{ marginLeft: 'auto', color: 'var(--hp-gold)', fontSize: '.78rem', display: 'inline-flex', alignItems: 'center', gap: 3 }}>Open <ExternalLink size={11} /></Link>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {(o.drivers || []).map((d) => (
              <span key={d.login} style={{ fontSize: '.74rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: d.acked_at ? '#15803d' : '#b91c1c' }}>
                {d.login} {d.acked_at ? '✓ today' : 'not confirmed today'}
              </span>
            ))}
            {(o.leaders || []).map((d) => (
              <span key={d.login} style={{ fontSize: '.74rem', fontFamily: 'var(--font-mono)', color: d.acked_at ? '#15803d' : '#b91c1c' }}>TL {d.login} {d.acked_at ? '✓' : '— not read'}</span>
            ))}
          </div>
        </div>
      ))}

      {!open ? (
        !live.length && (
          <>
            <button className="btn btn-secondary btn-sm" onClick={start}><PackageCheck size={13} /> Record Authority to Leave{looksAd ? ' — disability' : ''}</button>
            <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 6 }}>
              Puts it on Standing Orders with this case&rsquo;s PDF and AP&rsquo;s emails as the evidence, and tells the driver on this street every morning to leave everything in the receptacle.
            </div>
          </>
        )
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          <div>
            <label style={labelStyle}>Address</label>
            <input className="input" style={inputStyle} value={f.address} onChange={(e) => set({ address: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Customer</label>
            <input className="input" style={inputStyle} value={f.customer} onChange={(e) => set({ customer: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Leave everything in</label>
            <input className="input" style={inputStyle} value={f.receptacle} onChange={(e) => set({ receptacle: e.target.value })} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.include_signature} onChange={(e) => set({ include_signature: e.target.checked })} />
            Includes signature items — the driver leaves them too, never cards them
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', fontWeight: 700, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.disability} onChange={(e) => set({ disability: e.target.checked })} />
            <Accessibility size={13} style={{ color: '#2563eb' }} /> Customer with a disability (A&amp;D accommodation)
          </label>
          {f.disability && (
            <div>
              <label style={labelStyle}>A&amp;D details (office only)</label>
              <textarea className="input" rows={3} style={{ ...inputStyle, width: '100%' }} value={f.ad_details} onChange={(e) => set({ ad_details: e.target.value })} />
            </div>
          )}
          <div>
            <label style={labelStyle}>Anything else the driver should know (optional)</label>
            <textarea className="input" rows={2} style={{ ...inputStyle, width: '100%' }} value={f.instruction} onChange={(e) => set({ instruction: e.target.value })} placeholder="e.g. The receptacle is the grey box beside the front gate." />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.alert} onChange={(e) => set({ alert: e.target.checked })} />
            Alert the driver(s) and their team leader now (push, or a text if they have no alerts on)
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>{busy ? <Loader size={13} /> : <PackageCheck size={13} />} Record ATL</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
          </div>
          <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}>
            Drivers are found from the route lists ({row.driver_login || 'the case driver'} first). Check them on Standing Orders after.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Take special care, off a case ────────────────────────────────────────────
// For an address where we let the customer down and they are watching (Paul, 29 Oct 2026 —
// 33 Mitchell St, Glebe: a StarTrack parcel scanned as signed for when nobody was home, then a
// formal VOC complaint). Nothing about where the parcel goes changes, so it is not an ATL:
// it is a red card on the round driver's shift screen every morning, a 6am push, and Got it.
// It sits on Standing Orders with the case as evidence, and reaches whoever runs the round
// now — the driver on this case may already have gone.
function CareFromCaseBox({ row, address, notify }) {
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const load = useCallback(async () => {
    try {
      const r = await soAdmin('caseAtl', { caseId: row.id, caseNumber: row.case_number });
      setOrders(r && r.success ? (r.orders || []).filter((o) => o.kind === 'care') : []);
    } catch { setOrders([]); }
  }, [row.id, row.case_number]);
  useEffect(() => { load(); }, [load]);

  // The driver's text is kept calm and about what to DO — no allegation, no account of what
  // went wrong (Paul, 29 Oct 2026). That goes in the office notes, which drivers never see,
  // and the customer's name never reaches the phone for this kind either.
  const start = () => {
    const signed = row.case_category === 'signature' || /sign/i.test(`${row.enquiry_sub_type || ''} ${row.summary || ''}`);
    setF({
      address,
      customer: row.addressee_name || row.customer_name || '',
      instruction: signed
        ? 'Please take extra care with every delivery to this address.\nHand the parcel to the customer and get their signature. If nobody is home, card it and take it to the Post Office.'
        : 'Please take extra care with every delivery to this address.\nIf you are unsure, card it rather than guess.',
      office_notes: `AP case ${row.case_number || ''}: ${row.summary || row.enquiry_sub_type || ''}`.trim(),
      alert: true,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!f.address.trim()) { notify?.('Which address?', 'error'); return; }
    if (!f.instruction.trim()) { notify?.('Say what went wrong and what the driver must do', 'error'); return; }
    setBusy(true);
    const res = await soAdmin('fromCase', {
      caseId: row.id, notify: f.alert,
      fields: { kind: 'care', customer: f.customer, instruction: f.instruction, office_notes: f.office_notes, from: { name: '', address: f.address } },
    });
    setBusy(false);
    if (!res.success) { notify?.(res.error || 'Could not record it', 'error'); if (res.id) load(); return; }
    setOpen(false);
    const how = res.sent && res.sent.results ? ` · alerted ${res.sent.results.map((x) => `${x.login} (${x.how})`).join(', ')}` : '';
    notify?.(`Take special care recorded — ${res.records} record${res.records === 1 ? '' : 's'} filed as evidence${how}`, 'success');
    load();
  };

  const live = (orders || []).filter((o) => o.status === 'active');
  return (
    <div style={{ border: `1px solid ${live.length ? '#dc2626' : 'var(--hp-border)'}`, borderRadius: 8, padding: 12 }}>
      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><ShieldAlert size={12} /> Take special care at this address</div>

      {live.map((o) => (
        <div key={o.id} style={{ border: '1px solid rgba(220,38,38,0.45)', background: 'rgba(220,38,38,0.06)', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', fontSize: '.84rem', fontWeight: 700 }}>
            On Standing Orders since {fmtDateTime(o.created_at)} — {(o.from && o.from.address || '').split(',')[0]}
            <Link to={`/standing-orders?open=${o.id}`} style={{ marginLeft: 'auto', color: 'var(--hp-gold)', fontSize: '.78rem', display: 'inline-flex', alignItems: 'center', gap: 3 }}>Open <ExternalLink size={11} /></Link>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {(o.drivers || []).map((d) => (
              <span key={d.login} style={{ fontSize: '.74rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: d.acked_at ? '#15803d' : '#b91c1c' }}>
                {d.login}{d.auto ? ' (round now)' : ''} {d.acked_at ? '✓ today' : 'not confirmed today'}
              </span>
            ))}
            {(o.leaders || []).map((d) => (
              <span key={d.login} style={{ fontSize: '.74rem', fontFamily: 'var(--font-mono)', color: d.acked_at ? '#15803d' : '#b91c1c' }}>TL {d.login} {d.acked_at ? '✓' : '— not read'}</span>
            ))}
          </div>
        </div>
      ))}

      {!open ? (
        !live.length && (
          <>
            <button className="btn btn-secondary btn-sm" onClick={start}><ShieldAlert size={13} /> Take special care here</button>
            <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 6 }}>
              For a customer we have let down. The driver who runs this round (whoever that is) gets a calm reminder every morning, with no customer name, and taps Got it. The case goes on the paper trail.
            </div>
          </>
        )
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          <div>
            <label style={labelStyle}>Address</label>
            <input className="input" style={inputStyle} value={f.address} onChange={(e) => set({ address: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Customer</label>
            <input className="input" style={inputStyle} value={f.customer} onChange={(e) => set({ customer: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>What the driver must do (the driver reads this — keep it calm, no names or allegations)</label>
            <textarea className="input" rows={3} style={{ ...inputStyle, width: '100%' }} value={f.instruction} onChange={(e) => set({ instruction: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>What happened (office only — drivers never see this)</label>
            <textarea className="input" rows={3} style={{ ...inputStyle, width: '100%' }} value={f.office_notes} onChange={(e) => set({ office_notes: e.target.value })} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.alert} onChange={(e) => set({ alert: e.target.checked })} />
            Alert the driver and their team leader now (push, or a text if they have no alerts on)
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>{busy ? <Loader size={13} /> : <ShieldAlert size={13} />} Record it</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
          </div>
          <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}>
            The driver is found from the route lists. {row.driver_login || 'The case driver'} is included only while they still have a round.
          </div>
        </div>
      )}
    </div>
  );
}

function DeliveryRequestBox({ caseId, address, seed, driver, hasCasePhoto, requests, notify, onChanged }) {
  const [text, setText] = useState('');
  const [useCasePhotos, setUseCasePhotos] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const pick = async (f) => {
    if (!f) return;
    if (!/^image\//i.test(f.type)) { notify?.('Pick an image', 'error'); return; }
    try {
      const ab = await uprightJpegBytes(f, 1600, 0.82);
      const base64 = ab ? arrayBufferToBase64(ab) : (await fileToBase64(f)).base64;
      const contentType = ab ? 'image/jpeg' : (f.type || 'image/jpeg');
      if (base64.length * 0.75 > MAX_MB * 1024 * 1024) { notify?.(`That image is over ${MAX_MB} MB even after shrinking`, 'error'); return; }
      setFile({ base64, contentType, preview: `data:${contentType};base64,${base64}` });
    } catch { notify?.('Could not read that image', 'error'); }
  };

  const send = async () => {
    if (!text.trim()) { notify?.('Say what the customer has asked for', 'error'); return; }
    setBusy(true);
    const res = await api('addDeliveryRequest', {
      id: caseId, request: text.trim(), address, useCasePhotos,
      fileBase64: file ? file.base64 : '', contentType: file ? file.contentType : '',
    });
    setBusy(false);
    if (!res.success) { notify?.(res.error || 'Could not raise the request', 'error'); return; }
    setText(''); setFile(null); setUseCasePhotos(false); setOpen(false);
    if (inputRef.current) inputRef.current.value = '';
    notify?.(res.alerted ? `On ${driver}'s phone — alerted, waiting on their Got it`
      : res.smsSent ? `On ${driver}'s phone — texted, waiting on their Got it`
      : `On ${driver}'s phone — but they could NOT be alerted. Tell them another way.`,
      res.alerted || res.smsSent ? 'success' : 'error');
    onChanged?.();
  };

  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12 }}>
      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><MapPin size={12} /> Standing request for this address</div>

      {requests.length > 0 && (
        <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {requests.map((r) => (
            <div key={r.id} style={{ border: `1px solid ${r.acked_at ? 'rgba(22,163,74,0.45)' : 'rgba(220,38,38,0.45)'}`, background: r.acked_at ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.07)', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '.74rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: r.acked_at ? '#15803d' : '#b91c1c' }}>
                  {r.acked_at ? `${r.driver_login} confirmed ${fmtDateTime(r.acked_at)}` : `${r.driver_login} has NOT read this yet`}
                </span>
                <button className="btn btn-ghost btn-sm" disabled={busy} title="Take it off their phone (the record is kept)"
                  onClick={async () => {
                    setBusy(true);
                    const res = await api('retireDeliveryRequest', { requestId: r.id });
                    setBusy(false);
                    if (res.success) { notify?.('Retired', 'success'); onChanged?.(); }
                    else notify?.(res.error || 'Failed', 'error');
                  }}
                  style={{ marginLeft: 'auto', padding: '1px 5px', color: '#b91c1c' }}><X size={12} /></button>
              </div>
              <div style={{ fontSize: '.84rem', marginTop: 3, whiteSpace: 'pre-wrap' }}>{r.request}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>{r.address}</div>
            </div>
          ))}
        </div>
      )}

      {!open ? (
        <button className="btn btn-secondary btn-sm" onClick={() => { setOpen(true); if (!text) setText(seed || ''); }}>
          <Plus size={13} /> {requests.length ? 'Add another request' : 'Make this a standing request'}
        </button>
      ) : (
        <>
          <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
            What should {driver || 'the driver'} do at <strong>{(address || '').split(',')[0]}</strong> from now on? They must tap
            &ldquo;Got it&rdquo; before this clears, and it stays on their phone for them to look up later.
          </div>
          <textarea className="input" style={{ ...inputStyle, minHeight: 70, width: '100%' }} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Leave parcels behind the pot plant, away from the street — as the sign at the property asks." />
          {hasCasePhoto && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: '.8rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={useCasePhotos} onChange={(e) => setUseCasePhotos(e.target.checked)} />
              Send the delivery photo from this case with it
            </label>
          )}
          <input ref={inputRef} type="file" accept="image/*" onChange={(e) => pick(e.target.files && e.target.files[0])} style={{ fontSize: '.8rem', marginTop: 8 }} />
          {file && <img src={file.preview} alt="to send" style={{ width: 120, borderRadius: 8, border: '1px solid var(--hp-border)', display: 'block', marginTop: 8 }} />}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn btn-primary btn-sm" disabled={busy || !text.trim()} onClick={send}><Send size={13} /> Send to {driver || 'the driver'}</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => { setOpen(false); setText(''); }}>Cancel</button>
          </div>
          <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 6 }}>
            Their team leader is told as well — they cover the round when {driver || 'the driver'} is off.
          </div>
        </>
      )}
    </div>
  );
}

// ── Send a photo to the driver ───────────────────────────────────────────────
// Australia Post keeps sending images through mid-case, and there was nowhere to put one:
// the office could see it, the driver could not, and "do you remember anything odd about
// this?" had to be asked about a picture only one of them was looking at.
//
// The caption IS the question — it lands on the driver's card with the image, and they
// answer in the reply box already on that card. No second answer channel.
function DriverPhotoBox({ caseId, photos, driver, helper, open, notify, onChanged }) {
  // A list — AP often sends a set, and the driver should see them together under one question.
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState('');
  const [to, setTo] = useState('driver');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pick = async (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    const next = [...files];
    let used = next.reduce((n, x) => n + x.base64.length * 0.75, 0);
    for (const f of picked) {
      if (!/^image\//i.test(f.type)) { notify?.(`${f.name} isn't an image — skipped`, 'error'); continue; }
      try {
        // Upright and shrunk before it goes up: a phone photo is both sideways and far past
        // the 6 MB function-body limit once base64 has inflated it.
        const ab = await uprightJpegBytes(f, 1600, 0.82);
        const base64 = ab ? arrayBufferToBase64(ab) : (await fileToBase64(f)).base64;
        const contentType = ab ? 'image/jpeg' : (f.type || 'image/jpeg');
        const bytes = base64.length * 0.75;
        // All of them go in one POST, so the cap is on the set, not each picture.
        if (used + bytes > MAX_MB * 1024 * 1024) { notify?.(`${f.name} would take this send past ${MAX_MB} MB — send these first, then add it`, 'error'); continue; }
        used += bytes;
        next.push({ base64, contentType, preview: `data:${contentType};base64,${base64}`, name: f.name });
      } catch { notify?.(`Could not read ${f.name}`, 'error'); }
    }
    setFiles(next);
    if (inputRef.current) inputRef.current.value = '';
  };

  const send = async () => {
    if (!files.length) return;
    setBusy(true);
    const res = await api('sendDriverPhoto', {
      id: caseId, files: files.map((f) => ({ base64: f.base64, contentType: f.contentType })),
      caption: caption.trim(), audience: helper ? to : 'driver',
    });
    setBusy(false);
    if (!res.success) { notify?.(res.error || 'Could not send the photo', 'error'); return; }
    const n = files.length;
    setFiles([]); setCaption('');
    if (inputRef.current) inputRef.current.value = '';
    const said = [];
    if (res.driverNotified) said.push(res.alerted ? `${driver} alerted` : res.smsSent ? `${driver} texted` : `${driver} NOT reached`);
    if (res.assistLogin) said.push(res.assistAlerted ? `${res.assistLogin} alerted` : res.assistSms ? `${res.assistLogin} texted` : `${res.assistLogin} NOT reached`);
    const ok = !said.some((t) => /NOT reached/.test(t));
    const what = n > 1 ? `${n} photos on their card` : 'Photo on their card';
    notify?.(said.length ? `${what} — ${said.join(' · ')}` : what, ok ? 'success' : 'error');
    onChanged?.(res.photos || []);
  };

  const unsend = async (photoId) => {
    setBusy(true);
    const res = await api('unsendDriverPhoto', { id: caseId, photoId });
    setBusy(false);
    if (res.success) { notify?.('Taken off their card', 'success'); onChanged?.(res.photos || []); }
    else notify?.(res.error || 'Could not remove it', 'error');
  };

  return (
    <div>
      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><Camera size={12} /> Send photos to the driver</div>
      <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
        New images off Australia Post (pick several at once), or anything you want {driver || 'the driver'} to look at. What you type is the question they see with it.
      </div>

      {photos.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {photos.map((p) => (
            <div key={p.id} style={{ width: 132, border: '1px solid var(--hp-border)', borderRadius: 8, overflow: 'hidden' }}>
              <a href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt="sent to driver" style={{ width: '100%', height: 96, objectFit: 'cover', display: 'block' }} /></a>
              <div style={{ padding: '5px 7px' }}>
                {p.caption && <div style={{ fontSize: '.72rem', lineHeight: 1.35 }}>{p.caption}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <span style={{ fontSize: '.66rem', color: 'var(--hp-dim)' }}>{p.at ? fmtDateTime(p.at) : ''}</span>
                  <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => unsend(p.id)} title="Take it off their card and delete it"
                    style={{ marginLeft: 'auto', padding: '1px 5px', color: '#b91c1c' }}><X size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!open && (
        <div style={{ fontSize: '.78rem', color: '#b45309', marginBottom: 8 }}>
          This case is closed — a photo sent now sits in {driver || 'the driver'}&rsquo;s history and alerts nobody.
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={(e) => pick(e.target.files)}
        style={{ fontSize: '.8rem', marginBottom: 8 }} />
      {files.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          {files.map((f, i) => (
            <div key={i} style={{ position: 'relative', width: 132 }}>
              <img src={f.preview} alt={`to send ${i + 1}`} style={{ width: 132, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)', display: 'block' }} />
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setFiles(files.filter((_, j) => j !== i))} title="Don't send this one"
                style={{ position: 'absolute', top: 3, right: 3, padding: '1px 5px', background: 'rgba(0,0,0,0.65)', color: '#fff' }}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <input className="input" style={inputStyle} value={caption} onChange={(e) => setCaption(e.target.value)}
        placeholder="What are you asking them? e.g. Do you recall anything unusual about this drop?" />
      {helper && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)' }}>Alert</span>
          {[['driver', driver || 'the driver'], ['assist', `${helper} (recovery)`], ['both', 'Both']].map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setTo(k)}
              style={{ fontSize: '.76rem', fontWeight: 700, padding: '4px 10px', borderRadius: 14, cursor: 'pointer',
                border: `1px solid ${to === k ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                background: to === k ? 'rgba(201,168,76,0.15)' : 'transparent', color: 'var(--hp-text)' }}>{lbl}</button>
          ))}
        </div>
      )}
      <button className="btn btn-secondary" disabled={busy || !files.length} onClick={send} style={{ marginTop: 8 }}>
        <Send size={14} /> Send{files.length > 1 ? ` ${files.length} photos` : ''} to {helper && to === 'assist' ? helper : helper && to === 'both' ? 'both' : (driver || 'the driver')}
      </button>
    </div>
  );
}

// Office triage: compose the ONE note the driver sees on the case.
//
// Nothing on an AP case reaches the driver's portal by itself any more (Paul, 24 Aug 2026).
// The customer's verbatim words used to be piped straight through and they routinely
// confused more than they helped — half of a phone call, written by someone who wasn't
// there, often contradicting the scan. So the office decides. Each source line the
// extractor pulled off the case gets an **Include** button that drops it into the note,
// where it can be reworded, trimmed or deleted; the box is free text and can equally be
// written from scratch. Only what's saved is pushed.
// ── What the driver sees ─────────────────────────────────────────────────────
// An AP case arrives as a whole case file, and it used to reach the driver's portal simply
// by existing — the summary, every scan event, the signature name, the safe-drop photos.
// Nobody chose any of it (Paul, 28 Aug 2026). Now each piece is a switch, off until the
// office turns it on, exactly like the note, the questionnaire and the scan snapshot.
// The case number, the address, what kind of enquiry it is and the delivery date always
// go — that is the job, not the paperwork.
function DriverShareBox({ caseId, share, row, imageCount, notify, onSaved }) {
  const [busy, setBusy] = useState('');
  const val = share || { summary: false, photos: false, signature: false, timeline: false };
  const sig = (row.signature_name || '').trim();
  const items = [
    { key: 'signature', label: sig ? `Ask "Who is ${sig}?"` : 'Ask about the signature',
      hint: sig ? (/verbal/i.test(sig) ? 'A verbal drop — the portal asks who gave them the go-ahead and where they left it.' : 'The name captured on the scan, for them to identify.') : 'No signature name on this case.',
      has: !!sig },
    { key: 'photos', label: `Delivery photo${imageCount === 1 ? '' : 's'}${imageCount ? ` (${imageCount})` : ''}`,
      hint: imageCount ? 'The safe-drop photo AP attached — often the fastest way to jog their memory.' : 'No photo on this case.',
      has: imageCount > 0 },
    { key: 'summary', label: "Australia Post's summary line",
      hint: (row.summary || '').trim() || 'No summary on this case.', has: !!(row.summary || '').trim() },
    { key: 'timeline', label: `Delivery scan events${(row.event_timeline || []).length ? ` (${row.event_timeline.length})` : ''}`,
      hint: 'The full scan trail. The delivery date itself always shows.', has: (row.event_timeline || []).length > 0 },
  ];

  const toggle = async (key) => {
    const next = { ...val, [key]: !val[key] };
    setBusy(key);
    const res = await api('setDriverShare', { id: caseId, share: next });
    setBusy('');
    if (res.success) { onSaved?.(res.driver_share); }
    else notify?.(res.error || 'Could not save', 'error');
  };

  const on = items.filter((i) => val[i.key] && i.has).length;
  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12 }}>
      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><Eye size={12} /> What the driver sees</div>
      <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginBottom: 9 }}>
        They always get the case number, the address, the type of enquiry and the delivery date. Everything else off the case file is yours to send — {on === 0 ? 'nothing extra is going yet' : `${on} pushed`}.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((it) => (
          <label key={it.key}
            title={it.has ? undefined : 'Nothing on this case to send'}
            style={{ display: 'flex', gap: 9, alignItems: 'flex-start', border: '1px solid var(--hp-border)', borderRadius: 6, padding: '7px 9px', cursor: it.has ? 'pointer' : 'not-allowed', opacity: it.has ? 1 : .5, background: val[it.key] && it.has ? 'rgba(22,163,74,.06)' : 'transparent' }}>
            <input type="checkbox" checked={!!val[it.key] && it.has} disabled={!it.has || busy === it.key} onChange={() => toggle(it.key)} style={{ marginTop: 3 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '.84rem', fontWeight: 600 }}>{it.label}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.hint}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function DriverNoteBuilder({ caseId, initial, pushedAt, sources, notify, onPushed }) {
  const [note, setNote] = useState(initial || '');
  const [saved, setSaved] = useState(initial || '');
  const [at, setAt] = useState(pushedAt || null);
  const [saving, setSaving] = useState(false);

  // Re-seed when the drawer swings to another case.
  useEffect(() => { setNote(initial || ''); setSaved(initial || ''); setAt(pushedAt || null); }, [caseId, initial, pushedAt]);

  const dirty = note.trim() !== (saved || '').trim();
  const include = (text) => setNote((n) => {
    const t = (text || '').trim();
    if (!t || n.includes(t)) return n;              // never double-paste the same line
    return n.trim() ? `${n.trim()}\n${t}` : t;
  });

  const save = async () => {
    setSaving(true);
    const body = note.trim();
    const res = await api('setDriverNote', { id: caseId, note: body });
    setSaving(false);
    if (res.success) {
      setSaved(body);
      setAt(res.driver_note_at || (body ? new Date().toISOString() : null));
      onPushed?.({ driver_note: body, driver_note_at: res.driver_note_at || null });
      // Say whether the driver was actually TOLD. A note nobody is alerted to is how a
      // question sat unanswered for a day.
      notify?.(!body ? 'Note cleared'
        : !res.notified ? 'Note saved — unchanged, so nobody was alerted again'
        : res.alerted ? 'Note pushed — the driver\u2019s phone alerted'
        : res.smsSent ? 'Note pushed — the driver texted'
        : 'Note saved, but the driver could not be alerted (no app, no mobile). Tell them another way.',
        !body || res.notified === false || res.alerted || res.smsSent ? 'success' : 'error');
    } else notify?.(res.error || 'Save failed', 'error');
  };

  // Same note, alerted again — for the driver who hasn't opened the app since it was written.
  // Saving alone deliberately doesn't re-alert, so this is the way to chase one.
  const alertAgain = async () => {
    setSaving(true);
    const res = await api('setDriverNote', { id: caseId, note: saved, force: true });
    setSaving(false);
    if (!res.success) return notify?.(res.error || 'Failed', 'error');
    notify?.(res.alerted ? 'Driver\u2019s phone alerted again'
      : res.smsSent ? 'Driver texted again'
      : 'Could not reach them (no app, no mobile)', res.alerted || res.smsSent ? 'success' : 'error');
  };

  const avail = (sources || []).filter((x) => (x.text || '').trim());

  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12 }}>
      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><MessageSquare size={12} /> Note for the driver</div>
      <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
        The driver sees this and nothing else from the case paperwork. Include the lines that help them, edit them freely, or write your own. Leave it empty and they get no note.
      </div>

      {avail.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
          {avail.map((x, i) => {
            const inNote = note.includes(x.text.trim());
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, border: '1px solid var(--hp-border)', borderRadius: 6, padding: '6px 8px', background: inNote ? 'rgba(22,163,74,.06)' : 'transparent' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.64rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)' }}>{x.label}</div>
                  <div style={{ fontSize: '.82rem' }}>{x.text}</div>
                </div>
                <button className="btn btn-ghost btn-sm" disabled={inNote} onClick={() => include(x.text)} style={{ flexShrink: 0 }}>
                  {inNote ? 'Included' : 'Include'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <textarea className="input" style={{ ...inputStyle, minHeight: 80, fontFamily: 'inherit', fontSize: '.86rem' }}
        value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="What does the driver actually need to know about this case?" />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button className="btn btn-primary" disabled={saving || !dirty} onClick={save}>
          <Send size={14} /> {saving ? 'Saving…' : note.trim() ? 'Push note to driver' : 'Clear the note'}
        </button>
        {!dirty && saved.trim() && (
          <button className="btn btn-ghost btn-sm" disabled={saving} onClick={alertAgain}
            title="Same note, but ping their phone again — for a driver who hasn't opened the app since it was written.">
            <MessageSquare size={13} /> Alert again
          </button>
        )}
        {dirty
          ? <PushState tone="unsaved" label="Not pushed — unsaved edits" title="What's in the box has not gone to the driver. Press the button to push it." />
          : saved.trim()
            ? <PushState tone="pushed" label="Pushed to driver" when={at} title={at ? 'The driver has seen this note on their portal since this time.' : 'Pushed before the app recorded push times, so the time is unknown.'} />
            : <PushState tone="unpushed" label="No note pushed to the driver" title="The driver has no note on this case." />}
      </div>
    </div>
  );
}

// Office triage: tick which catalog questions (+ custom) to push to the driver's portal.
// When nothing's been saved yet, a sensible default set is pre-ticked so pushing is one click.
const DEFAULT_QUESTION_IDS = ['recall', 'usual_location', 'where_located', 'level'];
const qSig = (list) => JSON.stringify((list || []).map((q) => (q.custom ? `c:${q.prompt}` : `q:${q.id}`)).sort());
function QuestionnaireBuilder({ caseId, initial, pushedAt, hasPhoto, answered, notify, onPushed }) {
  const [selected, setSelected] = useState(() => {
    const saved = (initial || []).filter((q) => !q.custom).map((q) => q.id);
    return new Set(saved.length ? saved : DEFAULT_QUESTION_IDS);
  });
  const [customs, setCustoms] = useState((initial || []).filter((q) => q.custom).map((q) => q.prompt));
  const [newCustom, setNewCustom] = useState('');
  const [saving, setSaving] = useState(false);
  // What the driver actually has right now, so the badge below can tell "ticked here" from
  // "pushed to their portal" — the two drift apart the moment anyone touches a checkbox.
  const [pushed, setPushed] = useState(initial || []);
  const [at, setAt] = useState(pushedAt || null);
  useEffect(() => { setPushed(initial || []); setAt(pushedAt || null); }, [caseId, initial, pushedAt]);

  const current = [
    ...CATALOG.filter((q) => selected.has(q.id)).map((q) => ({ id: q.id })),
    ...[...new Set([...customs, newCustom.trim()].filter(Boolean))].map((prompt) => ({ prompt, custom: true })),
  ];
  const dirty = qSig(current) !== qSig(pushed);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const addCustom = () => { const t = newCustom.trim(); if (t) { setCustoms((c) => [...c, t]); setNewCustom(''); } };

  const save = async () => {
    setSaving(true);
    const picked = CATALOG.filter((q) => selected.has(q.id)).map((q) => ({ ...q }));
    // A question typed into the box but not yet "Add"ed is still a question the office wants
    // asked — save it rather than silently dropping it. Ids are derived from the text so a
    // re-save never re-uses an id the driver already answered.
    const allCustoms = [...new Set([...customs, newCustom.trim()].filter(Boolean))];
    const customQs = allCustoms.map((prompt) => ({ id: customQuestionId(prompt), prompt, type: 'text', custom: true }));
    const questionnaire = [...picked, ...customQs];
    const res = await api('setQuestionnaire', { id: caseId, questionnaire });
    setSaving(false);
    if (res.success) {
      setCustoms(allCustoms); setNewCustom('');
      setPushed(res.questionnaire || questionnaire);
      setAt(res.questionnaire_at || (questionnaire.length ? new Date().toISOString() : null));
      onPushed?.({ questionnaire: res.questionnaire || questionnaire, questionnaire_at: res.questionnaire_at || null });
      notify?.(questionnaire.length ? 'Questionnaire pushed to the driver' : 'Questionnaire cleared', 'success');
    } else notify?.(res.error || 'Save failed', 'error');
  };

  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12 }}>
      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><ListChecks size={12} /> Build driver questionnaire</div>
      <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginBottom: 8 }}>Tick the questions to push. The driver answers them on their portal — “Any other detail?” + Submit is added automatically.</div>
      {answered && (
        <div style={{ fontSize: '.76rem', color: '#b45309', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 6, padding: '6px 8px', marginBottom: 8 }}>
          This driver has already answered. Anything you add here is pushed back to their portal as a follow-up question.
        </div>
      )}
      {CATALOG.map((q) => (
        <label key={q.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0', fontSize: '.86rem', cursor: 'pointer', opacity: (q.requiresPhoto && !hasPhoto) ? 0.55 : 1 }}>
          <input type="checkbox" checked={selected.has(q.id)} onChange={() => toggle(q.id)} style={{ marginTop: 3 }} />
          <span>
            {q.prompt.replace('{usual_location}', 'their choice')}
            {q.type === 'choice' && <span style={{ color: 'var(--hp-dim)' }}> — {q.options.join(' / ')}</span>}
            {q.requiresPhoto && <span style={{ color: 'var(--hp-dim)' }}> {hasPhoto ? '· a photo is attached' : '· only shows if a photo is attached'}</span>}
          </span>
        </label>
      ))}
      {customs.map((t, i) => (
        <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', fontSize: '.86rem' }}>
          <span style={{ flex: 1 }}>• {t}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setCustoms((c) => c.filter((_, j) => j !== i))}><X size={12} /></button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input className="input" style={{ ...inputStyle, fontSize: '.84rem' }} value={newCustom} onChange={(e) => setNewCustom(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} placeholder="Add a custom question…" />
        <button className="btn btn-secondary btn-sm" onClick={addCustom} disabled={!newCustom.trim()}>Add</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          <Send size={14} /> {saving ? 'Pushing…' : 'Push questions to driver'}
        </button>
        {!pushed.length
          ? <PushState tone="unpushed" label="No questions pushed to the driver" title="Nothing on this case is waiting for the driver to answer." />
          : dirty
            ? <PushState tone="unsaved" label="Changes not pushed yet" title={`The driver still has the ${pushed.length} question${pushed.length > 1 ? 's' : ''} last pushed. Press the button to push what's ticked now.`} />
            : <PushState tone="pushed" label={`${pushed.length} question${pushed.length > 1 ? 's' : ''} pushed`} when={at} title={at ? "These are on the driver's portal." : 'Pushed before the app recorded push times, so the time is unknown.'} />}
      </div>
    </div>
  );
}

// ── Carry-over incident report on a case ────────────────────────────────────
// The customer has raised a case about a parcel we already reported lost / stolen in a
// carry-over email. Attach that report here: the write-up and its photos, plus when the
// email went and to whom. "Use in reply" puts it into the AP reply (text + photos ticked),
// and the driver can be told about the case without being asked to answer it.
function IncidentReportBox({ caseId, row, incident, notify, onChanged, onUseInReply }) {
  const [finding, setFinding] = useState(false);
  const [found, setFound] = useState(null);     // candidates, once looked up
  const [busy, setBusy] = useState('');

  const look = async () => {
    setFinding(true);
    const res = await api('caseIncidents', { id: caseId });
    setFinding(false);
    if (res.success) setFound(res.incidents || []);
    else notify?.(res.error || 'Could not look up incident reports', 'error');
  };
  const attach = async (c) => {
    setBusy(c.id);
    const res = await api('attachIncident', { id: caseId, date: c.date, incidentId: c.id });
    setBusy('');
    if (res.success) { setFound(null); onChanged?.(res.incident); notify?.('Incident report attached to the case', 'success'); }
    else notify?.(res.error || 'Could not attach it', 'error');
  };
  const detach = async () => {
    if (!window.confirm('Take this incident report off the case?\n\nThe report itself stays on Direct Entry — this only unlinks it.')) return;
    setBusy('detach');
    const res = await api('detachIncident', { id: caseId });
    setBusy('');
    if (res.success) { onChanged?.(null); notify?.('Incident report taken off the case', 'success'); }
    else notify?.(res.error || 'Could not remove it', 'error');
  };
  const tellDriver = async () => {
    const who = incident?.incident?.driver || row.driver_login || row.ap_driver_login || 'the driver';
    const again = incident?.driverAlertedAt ? `\n\n${who} was already told on ${fmtDateTime(incident.driverAlertedAt)} — this tells them again.` : '';
    if (!window.confirm(`Let ${who} know the customer has raised this case about the parcel they reported missing?\n\nThey're told no reply is needed. The case is marked answered so the daily "please reply" reminder doesn't go to them.${!row.driver_login ? `\n\nThe case is confirmed to ${who} too (quietly — no "tap to answer" alert).` : ''}${again}`)) return;
    setBusy('alert');
    const res = await api('alertIncidentDriver', { id: caseId });
    setBusy('');
    if (res.success) {
      const how = res.alerted ? 'push sent' : res.smsSent ? 'texted' : 'no phone reached: they will see it next time they open the app';
      notify?.(`${res.driver} told (${how})${res.confirmed ? ' · case confirmed to them' : ''}`, res.alerted || res.smsSent ? 'success' : 'error');
      onChanged?.({ ...incident, driverAlertedAt: new Date().toISOString() });
    } else notify?.(res.error || 'Could not alert the driver', 'error');
  };

  // The carry-over email as an .eml, to open and check what AP received.
  const downloadEmail = async () => {
    setBusy('eml');
    const res = await api('carryoverEmail', { id: caseId });
    setBusy('');
    if (!res.success) { notify?.(res.error || 'Could not get the email', 'error'); return; }
    const bytes = Uint8Array.from(atob(res.base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'message/rfc822' }));
    const a = document.createElement('a');
    a.href = url; a.download = res.filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const box = { border: '1px solid var(--hp-border)', borderLeft: '4px solid #b42318', borderRadius: 8, padding: 12 };
  const head = <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={12} /> Incident report</div>;

  if (!incident) {
    return (
      <div style={{ ...box, borderLeft: '1px solid var(--hp-border)' }}>
        {head}
        <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
          Customer asking about a parcel we already reported missing, stolen or damaged in a carry-over email? Attach that report so it goes in the reply.
        </div>
        {!found ? (
          <button className="btn btn-secondary btn-sm" disabled={finding} onClick={look}>
            {finding ? <><Loader size={13} /> Looking…</> : <><Package size={13} /> Attach a missing-parcel report</>}
          </button>
        ) : found.length === 0 ? (
          <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>
            No incident report found for this parcel or {row.driver_login || row.ap_driver_login || 'its driver'} in the 45 days before the case. Write one up on Direct Entry → Status → Carry-over.
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={() => setFound(null)}>Close</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {found.map((c) => (
              <div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: '.82rem' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <strong style={{ color: '#b42318' }}>{c.type}</strong>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{c.driver}</span>
                    <span style={{ color: 'var(--hp-dim)' }}>{fmtDate(c.date)}</span>
                    <span style={{ fontSize: '.68rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: c.match === 'parcel' ? 'rgba(22,163,74,0.12)' : 'rgba(217,119,6,0.12)', color: c.match === 'parcel' ? '#16a34a' : '#d97706' }}>
                      {c.match === 'parcel' ? 'Same parcel' : 'Same driver — check the parcel'}
                    </span>
                  </div>
                  <div style={{ color: 'var(--hp-dim)', fontSize: '.76rem', marginTop: 2 }}>
                    {[c.consignment, c.articleId].filter(Boolean).join(' · ')}{c.receiver ? ` · To: ${c.receiver}` : ''}
                  </div>
                  {c.whatHappened && <div style={{ fontSize: '.78rem', marginTop: 2 }}>{c.whatHappened.length > 160 ? `${c.whatHappened.slice(0, 160)}…` : c.whatHappened}</div>}
                </div>
                <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => attach(c)}>{busy === c.id ? 'Attaching…' : 'Attach'}</button>
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setFound(null)}>Cancel</button>
          </div>
        )}
      </div>
    );
  }

  const inc = incident.incident || {};
  const em = incident.email;
  const rowsOf = [
    ['To', inc.receiver], ['What happened', inc.whatHappened], ['Action', inc.actionTaken],
  ].filter(([, v]) => v);
  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>{head}</div>
        <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={detach} title="Take the report off this case"><X size={12} /></button>
      </div>
      {incident.missing ? (
        <div style={{ fontSize: '.82rem', color: '#b91c1c' }}>The linked report for {fmtDate(incident.date)} is no longer on file. Take it off and attach again.</div>
      ) : (
        <>
          {incident.removed && <div style={{ fontSize: '.76rem', color: '#b91c1c', marginBottom: 6 }}>This report was removed on Direct Entry since it was attached.</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', fontSize: '.86rem' }}>
            <strong style={{ color: '#b42318' }}>{inc.type}</strong>
            <strong style={{ fontFamily: 'var(--font-mono)' }}>{inc.driver}</strong>
            <span style={{ color: 'var(--hp-dim)', fontSize: '.8rem' }}>
              {inc.count || 1} parcel{(inc.count || 1) > 1 ? 's' : ''}{inc.consignment ? ` · ${inc.consignment}` : ''}{inc.articleId ? ` · ${inc.articleId}` : ''} · carry-over {fmtDate(incident.date)}
            </span>
          </div>
          {rowsOf.map(([k, v]) => (
            <div key={k} style={{ fontSize: '.82rem', marginTop: 4 }}><span style={{ color: 'var(--hp-dim)' }}>{k}: </span>{v}</div>
          ))}
          {incident.photos?.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {incident.photos.map((p) => (
                <a key={p.ref} href={p.url} target="_blank" rel="noreferrer" title={p.caption || 'Incident photo'} style={{ lineHeight: 0 }}>
                  <img src={p.url} alt={p.caption || 'Incident photo'} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} />
                </a>
              ))}
            </div>
          )}
          <div style={{ fontSize: '.76rem', marginTop: 8, padding: '7px 9px', borderRadius: 6, background: 'var(--hp-surface)', lineHeight: 1.5 }}>
            {em && em.sentAt ? (
              <>
                <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ Sent to Australia Post {fmtDateTime(em.sentAt)}</span>{em.by ? ` by ${em.by}` : ''}
                <div><span style={{ color: 'var(--hp-dim)' }}>To:</span> <strong>{em.to}</strong></div>
                {em.cc && <div style={{ wordBreak: 'break-word' }}><span style={{ color: 'var(--hp-dim)' }}>Cc:</span> {em.cc}</div>}
                {em.subject && <div><span style={{ color: 'var(--hp-dim)' }}>Subject:</span> {em.subject}</div>}
                <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 0', marginTop: 2 }} disabled={!!busy} onClick={downloadEmail}>
                  <Mail size={12} /> {busy === 'eml' ? 'Getting it…' : 'Download the email'}
                </button>
              </>
            ) : <span style={{ color: '#d97706', fontWeight: 700 }}>The carry-over email for {fmtDate(incident.date)} hasn't been sent yet.</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={() => onUseInReply?.(incident)}><Send size={13} /> Use in the AP reply</button>
            <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={tellDriver}>
              {busy === 'alert' ? <><Loader size={13} /> Alerting…</> : <><Smartphone size={13} /> {incident.driverAlertedAt ? 'Tell driver again' : `Let ${inc.driver || 'the driver'} know (no reply needed)`}</>}
            </button>
            {incident.driverAlertedAt && <span style={{ fontSize: '.74rem', color: '#16a34a' }}>✓ Driver told {fmtDateTime(incident.driverAlertedAt)}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function CaseDrawer({ drawer, onClose, onSaved, notify, drivers = [], isAdmin = true }) {
  const [busy, setBusy] = useState(false);
  const [row, setRow] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [imageUrls, setImageUrls] = useState([]);
  const [imageRefs, setImageRefs] = useState([]);      // 'case:<i>' per photo above, for attaching
  const [imagePool, setImagePool] = useState([]);      // every photo off the PDF: {path,url,chosen}
  const [choosing, setChoosing] = useState(false);     // picking which of them are on the case
  const [attachRefs, setAttachRefs] = useState([]);    // photos on the case ticked to go with the reply
  const [attachFiles, setAttachFiles] = useState([]);  // extra images picked off this computer
  const [attachVideos, setAttachVideos] = useState([]); // clips already in the bucket, sent as watch links
  const [videoUp, setVideoUp] = useState(null);        // { name, frac } while one is uploading
  const [scannerShots, setScannerShots] = useState([]); // scanner screenshots already in the app
  const [imgPreviews, setImgPreviews] = useState([]); // delivery photos detected on the add screen
  const [keepImgs, setKeepImgs] = useState(new Set()); // which of them go on the case (all by default)
  const [responses, setResponses] = useState([]);
  const [driverShare, setDriverShare] = useState(null);
  const [driverPhotos, setDriverPhotos] = useState([]);
  const [driverSeen, setDriverSeen] = useState({});      // login → { first_at, last_at, count } — when they had the card open   // images the office has sent to the driver's card
  const [deliveryRequests, setDeliveryRequests] = useState([]);  // standing requests raised off this case
  // Whether this case is at an address already on the Pre-Card register, resolved
  // server-side against the live register on every open.
  const [precard, setPrecard] = useState(null);   // what the driver is allowed to see
  const [notes, setNotes] = useState([]);      // internal office notes — never sent anywhere
  const [incident, setIncident] = useState(null); // carry-over incident report attached to the case
  const [attachCarryover, setAttachCarryover] = useState(false); // send that carry-over email (.eml) with the reply
  // Refresher briefs: the catalogue the backend offers, which its category suggests, and
  // which have already gone to this driver (from analysis.trainingPushes on the case).
  const [briefs, setBriefs] = useState([]);
  const [suggestedBriefs, setSuggestedBriefs] = useState([]);
  const [pushingBrief, setPushingBrief] = useState('');
  const [noteText, setNoteText] = useState('');
  const [apDraft, setApDraft] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [toList, setToList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [replySubject, setReplySubject] = useState('');
  const [mySender, setMySender] = useState(null);
  const [newAddr, setNewAddr] = useState('');
  const [newToAddr, setNewToAddr] = useState('');
  const [sending, setSending] = useState(false);
  const [resolveOnSend, setResolveOnSend] = useState(true);
  const [otherOpen, setOtherOpen] = useState(false); // "Other — specify" fault reason input open
  const [specify, setSpecify] = useState('');
  const [edit, setEdit] = useState(drawer.mode === 'add');
  const [fields, setFields] = useState(drawer.fields || emptyFields());
  const [file, setFile] = useState(null);
  const [reading, setReading] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const [assignSel, setAssignSel] = useState('');
  const [respText, setRespText] = useState('');
  const [logOnly, setLogOnly] = useState(false);   // off = the box writes to the driver
  // Whose phone this message rings: the case's own driver, the recovery driver out looking
  // for the parcel, or both. Defaults to the owner — the office adds the helper deliberately
  // rather than a message quietly texting someone who wasn't being spoken to.
  const [msgTo, setMsgTo] = useState('driver');    // 'driver' | 'assist' | 'both'
  const [assistOpen, setAssistOpen] = useState(false);   // "send to another driver" panel
  const [assistSel, setAssistSel] = useState('');
  const [assistTask, setAssistTask] = useState('');
  const [assistErr, setAssistErr] = useState('');
  const [portalPreview, setPortalPreview] = useState(false);   // "Driver Portal View" modal

  // Arrays are edited as one-item-per-line textareas.
  const linesToArr = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);
  const arrToLines = (a) => (Array.isArray(a) ? a.join('\n') : '');

  const processFile = useCallback(async (f) => {
    if (!f) return;
    if (!isAllowedFile(f)) { notify?.('Please choose a PDF or image', 'error'); return; }
    if (f.size > MAX_MB * 1024 * 1024) { notify?.(`File too large (max ${MAX_MB} MB)`, 'error'); return; }
    setReading(true);
    try {
      const { base64, contentType, name } = await fileToBase64(f);
      setFile({ base64, contentType, name });
      const res = await api('extractFile', { fileBase64: base64, contentType });
      if (res.success && res.fields) {
        const x = res.fields;
        const keep = (cur, val) => (cur && String(cur).trim() ? cur : (val || ''));
        setFields((cur) => ({
          ...cur,
          case_number: keep(cur.case_number, x.case_number),
          enquiry_sub_type: keep(cur.enquiry_sub_type, x.enquiry_sub_type),
          case_category: x.case_category || cur.case_category,
          contract: keep(cur.contract, x.contract),
          tracking_no: keep(cur.tracking_no, x.tracking_no),
          customer_name: keep(cur.customer_name, x.customer_name),
          customer_role: x.customer_role || cur.customer_role,
          customer_address: keep(cur.customer_address, x.customer_address),
          customer_message: keep(cur.customer_message, x.customer_message),
          summary: keep(cur.summary, x.summary),
          description: keep(cur.description, x.description),
          ap_driver_login: keep(cur.ap_driver_login, x.ap_driver_login),
          signature_required: typeof x.signature_required === 'boolean' ? x.signature_required : cur.signature_required,
          sla_target_date: keep(cur.sla_target_date, x.sla_target_date),
          // The investigation form's cover "DATE:" — when AP sent us the case. Carried
          // through so a case added by hand dates from the form, not from the moment
          // somebody got round to typing it in.
          created_date: keep(cur.created_date, x.created_date),
          driver_questions: (cur.driver_questions && cur.driver_questions.length) ? cur.driver_questions : (x.driver_questions || []),
          quick_answers: (cur.quick_answers && cur.quick_answers.length) ? cur.quick_answers : (x.quick_answers || []),
          key_facts: (cur.key_facts && cur.key_facts.length) ? cur.key_facts : (x.key_facts || []),
        }));
        setImgPreviews(res.imagePreviews || []);
        setKeepImgs(new Set((res.imagePreviews || []).map((_, i) => i)));
        const photoNote = res.imageCount ? ` · ${res.imageCount} delivery photo${res.imageCount > 1 ? 's' : ''} captured` : '';
        notify?.((res.meta?.usedClaude ? 'Read the case and drafted the questions — please check' : 'File attached') + photoNote, 'success');
      } else notify?.(res.error || 'Could not read the file, but it will still be attached', 'error');
    } catch { notify?.('Failed to read the file', 'error'); }
    finally { setReading(false); }
  }, [notify]);

  useEffect(() => { if (drawer.file) processFile(drawer.file); }, [drawer.file, processFile]);

  // Signed storage URLs expire, so mint a fresh one at click time rather than trusting
  // the one fetched when the case was opened. Window opens first to dodge popup blockers.
  const openPdf = useCallback(async () => {
    const w = window.open('', '_blank');
    try {
      const res = await api('attachmentUrl', { id: drawer.id });
      if (res.success && res.url) { if (w) w.location = res.url; else window.open(res.url, '_blank'); }
      else { w?.close(); notify?.(res.error || 'Could not open the PDF', 'error'); }
    } catch { w?.close(); notify?.('Could not open the PDF', 'error'); }
  }, [drawer.id, notify]);

  useEffect(() => {
    if (drawer.mode !== 'view') return;
    (async () => {
      const res = await api('getCase', { id: drawer.id });
      if (res.success) {
        const s = res.case;
        setRow(s);
        setAttachmentUrl(res.attachmentUrl || '');
        setImageUrls(res.imageUrls || []);
        setImageRefs(res.imageRefs || []);
        setImagePool(res.imagePool || []);
        setChoosing(false);
        setAttachRefs([]); setAttachFiles([]);
        setScannerShots(res.scannerPhotos || []);
        setDriverShare(res.driverShare || null);
        setDriverPhotos(res.driverPhotos || []);
        setDriverSeen(res.driverSeen || {});
        loadRequests();
        setPrecard(res.precard || null);
        setApDraft('');
        setToList(res.reply?.to || []);
        setCcList(res.reply?.cc || []);
        setReplySubject(res.reply?.subject || '');
        setMySender(res.mySender || null);
        setNewAddr(''); setNewToAddr('');
        setOtherOpen(false);
        setSpecify(res.case.fault_reason || '');
        setResponses(res.responses || []);
        setNotes(res.notes || []);
        setIncident(res.incident || null);
        setAttachCarryover(false);
        setBriefs(res.briefs || []);
        setSuggestedBriefs(res.suggestedBriefs || []);
        setAssignSel(s.driver_login || suggestion(s).suggested_driver || s.ap_driver_login || '');
        setAssistOpen(false);
        setAssistSel(s.assist_status === 'open' ? (s.assist_login || '') : '');
        setAssistTask(s.assist_status === 'open' ? (s.assist_task || '') : defaultAssistTask(s));
        setFields({
          case_number: s.case_number, enquiry_sub_type: s.enquiry_sub_type, case_category: s.case_category,
          contract: s.contract, tracking_no: s.tracking_no, customer_name: s.customer_name,
          customer_role: s.customer_role, customer_address: s.customer_address, customer_message: s.customer_message,
          addressee_name: s.addressee_name || '', addressee_address: s.addressee_address || '',
          summary: s.summary, description: s.description, ap_driver_login: s.ap_driver_login,
          signature_required: s.signature_required, sla_target_date: s.sla_target_date || '',
          driver_questions: s.driver_questions || [], quick_answers: s.quick_answers || [], key_facts: s.key_facts || [],
        });
      } else notify?.(res.error || 'Not found', 'error');
    })();
  }, [drawer, notify]);

  // The photos currently ON the case, in order — the first is the main one.
  const chosenPaths = useMemo(() => imagePool.filter((p) => p.chosen).map((p) => p.path), [imagePool]);

  // Which of the PDF's photos belong on the case. The list is re-fetched from the server's
  // answer rather than assumed, so a rejected path (one not in the pool) can't linger on screen.
  const setCaseImages = useCallback(async (paths) => {
    setBusy(true);
    const res = await api('chooseCaseImages', { id: drawer.id, paths });
    setBusy(false);
    if (!res.success) { notify?.(res.error || 'Could not change the photos', 'error'); return; }
    const kept = res.chosen || [];
    setImagePool((pool) => pool.map((p) => ({ ...p, chosen: kept.includes(p.path) })));
    setImageUrls(kept.map((path) => (imagePool.find((p) => p.path === path) || {}).url).filter(Boolean));
    setImageRefs(kept.map((_, i) => `case:${i}`));
    setAttachRefs([]);                       // the refs just moved — don't send a stale tick to AP
    // Kept local on purpose: onSaved() closes the drawer, and being thrown out of the case
    // after every tick makes choosing between four photos impossible. The list doesn't show
    // case photos, so it has nothing to refresh for.
    setRow((r) => r && ({ ...r, case_images: kept }));
  }, [drawer.id, imagePool, notify]);

  const toggleImage = (path) => setCaseImages(
    chosenPaths.includes(path) ? chosenPaths.filter((p) => p !== path) : [...chosenPaths, path]
  );
  const makeMainImage = (path) => setCaseImages([path, ...chosenPaths.filter((p) => p !== path)]);

  const save = async () => {
    setBusy(true);
    const fileParams = file ? { fileBase64: file.base64, filename: file.name, contentType: file.contentType } : {};
    const keepParams = imgPreviews.length ? { keepImages: imgPreviews.map((_, i) => i).filter((i) => keepImgs.has(i)) } : {};
    const res = drawer.mode === 'add'
      ? await api('addManualCase', { fields, ...fileParams, ...keepParams })
      : await api('updateCase', { id: drawer.id, fields, ...fileParams, ...keepParams });
    setBusy(false);
    if (res.success) { notify?.(drawer.mode === 'add' ? 'Case added' : 'Saved', 'success'); onSaved(); }
    else notify?.(res.error || 'Save failed', 'error');
  };

  const confirmDriver = async (loginArg) => {
    const login = ((typeof loginArg === 'string' ? loginArg : assignSel) || '').trim().toUpperCase();
    if (!login) { notify?.('Pick or type a driver login', 'error'); return; }
    setBusy(true);
    const res = await api('confirmDriver', { id: drawer.id, driver_login: login });
    setBusy(false);
    if (!res.success) { notify?.(res.error || 'Failed', 'error'); return; }
    // Changing the driver on an already-confirmed case keeps you on the case: closing it made
    // a Change look like nothing had happened (Paul, 17 Sep 2026). A first confirm from triage
    // still closes, as it always has — that case is done with triage.
    const wasMatched = row && row.match_status === 'matched';
    notify?.(res.smsSent ? `${wasMatched ? 'Changed' : 'Confirmed'} to ${login} — texted them` : `${wasMatched ? 'Changed' : 'Confirmed'} to ${login}`, 'success');
    if (wasMatched) setRow((r) => r && ({ ...r, driver_login: login, match_status: 'matched' }));
    else onSaved();
  };

  // Recovery hand-off: the named driver stays on the case (their answer, their fault call,
  // their stats) but a second driver is asked to go and find the parcel. Used when the driver
  // AP named can't get there — on leave, off the round, already finished.
  // Never disable the send button on a missing driver — a dead button reads as "broken".
  // Say what's missing instead, and always clear `busy` so one failed call can't wedge it.
  const sendAssist = async () => {
    const login = (assistSel || '').trim().toUpperCase();
    if (!login) { setAssistErr('Choose the driver who will go and look, then press send.'); notify?.('Pick the driver who will do the recovery', 'error'); return; }
    setAssistErr('');
    setBusy(true);
    try {
      const res = await api('assignAssist', { id: drawer.id, driver_login: login, task: assistTask.trim() });
      if (res.success) {
        notify?.(res.smsSent ? `Sent to ${login} — texted them` : `Sent to ${login}`, 'success');
        setAssistOpen(false);
        const r = await api('getCase', { id: drawer.id });
        if (r.success) { setRow(r.case); setResponses(r.responses || []); }
      } else { setAssistErr(res.error || 'Failed'); notify?.(res.error || 'Failed', 'error'); }
    } catch (e) {
      setAssistErr(`Could not reach the server (${e.message}). Try again.`);
      notify?.('Could not reach the server — try again', 'error');
    } finally { setBusy(false); }
  };
  const cancelAssist = async () => {
    if (!window.confirm(`Stand ${row?.assist_login || 'the other driver'} down from this recovery?`)) return;
    setBusy(true);
    try {
      const res = await api('clearAssist', { id: drawer.id });
      if (res.success) {
        notify?.('Recovery stood down', 'success');
        const r = await api('getCase', { id: drawer.id });
        if (r.success) { setRow(r.case); setResponses(r.responses || []); setAssistTask(defaultAssistTask(r.case)); }
      } else notify?.(res.error || 'Failed', 'error');
    } catch { notify?.('Could not reach the server — try again', 'error'); }
    finally { setBusy(false); }
  };

  // Resolving answers the customer — it doesn't finish the case. Until someone has recorded
  // At fault / Not at fault the case stays in the office's triage list, so an admin is kept
  // in the drawer (the panel to do it is right there) and a team leader is told who owes it.
  const setStatus = async (status) => {
    setBusy(true);
    const res = await api('updateCase', { id: drawer.id, status });
    setBusy(false);
    if (!res.success) { notify?.(res.error || 'Failed', 'error'); return; }
    if (res.awaitingFault && isAdmin) {
      notify?.(`Marked ${status} — now record At fault / Not at fault to finish it`, 'info');
      const r = await api('getCase', { id: drawer.id });
      if (r.success) { setRow(r.case); setResponses(r.responses || []); setNotes(r.notes || []); }
      return;
    }
    notify?.(res.awaitingFault
      ? `Marked ${status} — it stays in triage until the office records the fault call`
      : `Marked ${status}`, 'success');
    onSaved();
  };

  const togglePhoto = async () => {
    const next = !(row && row.photo_requested);
    setBusy(true);
    const res = await api('updateCase', { id: drawer.id, fields: { photo_requested: next } });
    setBusy(false);
    if (res.success) { setRow(r => r && ({ ...r, photo_requested: next })); notify?.(next ? 'Photo requested from the driver' : 'Photo request cleared', 'success'); }
    else notify?.(res.error || 'Failed', 'error');
  };

  // Tick a refresher: the illustrated brief goes to the driver's My Docs, the matching quiz
  // becomes a required task in their portal, and they get a text saying it's there. Untick
  // takes the outstanding quiz back (a pass already earned is left alone).
  const pushedBriefs = useMemo(() => {
    const list = ((row && row.analysis && row.analysis.trainingPushes) || []);
    return Array.isArray(list) ? list.filter((p) => p && p.id) : [];
  }, [row]);

  const toggleBrief = async (brief) => {
    const on = !pushedBriefs.some((p) => p.id === brief.id);
    setPushingBrief(brief.id);
    const res = await api('pushTraining', { id: drawer.id, brief: brief.id, on });
    setPushingBrief('');
    if (!res.success) { notify?.(res.error || 'Failed', 'error'); return; }
    setRow((r) => r && ({ ...r, analysis: { ...(r.analysis || {}), trainingPushes: res.pushes || [] } }));
    const r2 = await api('getCase', { id: drawer.id });
    if (r2.success) setNotes(r2.notes || []);
    if (!on) { notify?.(`${brief.label} refresher withdrawn`, 'success'); return; }
    const bits = [];
    if (res.docId) bits.push('brief in My Docs');
    if (res.assignmentId) bits.push('quiz assigned');
    if (res.smsSent) bits.push('driver texted');
    if (res.quizMissing) bits.push('quiz not published yet');
    notify?.(`${brief.label} sent to ${row.driver_login}${bits.length ? ' — ' + bits.join(', ') : ''}`, 'success');
  };

  // Every picture already on the case — the delivery photos that came with it and
  // anything the driver answered with — offered for the reply.
  const casePhotos = useMemo(() => ([
    ...imageUrls.map((url, i) => ({ ref: imageRefs[i], url, who: 'On the case' })).filter((p) => p.ref),
    ...responses.flatMap((r) => (r.photoRefs || []).map((ref, i) => ({
      ref, url: (r.photos || [])[i], who: r.via === 'admin' ? 'Sent to AP' : (r.driver_login || 'Driver'),
    }))).filter((p) => p.ref && p.url),
    ...((incident && incident.photos) || []).map((p) => ({ ref: p.ref, url: p.url, who: 'Incident report' })),
  ]), [imageUrls, imageRefs, responses, incident]);
  const toggleAttach = (ref) => setAttachRefs((l) => (l.includes(ref) ? l.filter((x) => x !== ref) : [...l, ref]));

  // Pictures off the computer are re-encoded upright and shrunk before they go up —
  // a phone photo straight off the camera is both sideways and far past the 6 MB limit.
  const addAttachFiles = async (files) => {
    for (const f of Array.from(files || [])) {
      if (!/^image\//i.test(f.type)) { notify?.('Only images can be attached to the reply', 'error'); continue; }
      try {
        const ab = await uprightJpegBytes(f, 1600, 0.82);
        const base64 = ab ? arrayBufferToBase64(ab) : (await fileToBase64(f)).base64;
        const contentType = ab ? 'image/jpeg' : (f.type || 'image/jpeg');
        const bytes = Math.round(base64.length * 0.75);
        setAttachFiles((l) => {
          if (l.reduce((n, x) => n + x.bytes, 0) + bytes > MAX_MB * 1024 * 1024) {
            notify?.(`That would take the attached images past ${MAX_MB} MB — send fewer at a time`, 'error');
            return l;
          }
          return [...l, { name: f.name, contentType, base64, bytes, preview: `data:${contentType};base64,${base64}` }];
        });
      } catch { notify?.('Could not read that image', 'error'); }
    }
  };

  // A video goes straight into the bucket as soon as it's picked (too big for the
  // function or the email), so Send only names it and the reply carries a watch link.
  const addReplyVideo = async (file) => {
    const bad = checkVideo(file);
    if (bad) { notify?.(bad, 'error'); return; }
    if (attachVideos.length >= 2) { notify?.('Two videos at most on one reply', 'error'); return; }
    const type = file.type || (/\.mov$/i.test(file.name || '') ? 'video/quicktime' : 'video/mp4');
    setVideoUp({ name: file.name, frac: 0 });
    try {
      const duration = await videoDuration(file);
      const start = await api('replyVideoUploadUrl', { id: drawer.id, type, size: file.size });
      if (!start.success) throw new Error(start.error || 'Could not start the upload');
      await putFile(start.uploadUrl, file, type, (frac) => setVideoUp({ name: file.name, frac }));
      setAttachVideos((l) => [...l, { path: start.path, name: file.name, size: file.size, duration, preview: window.URL.createObjectURL(file) }]);
    } catch (e) { notify?.(e.message || 'Video upload failed', 'error'); }
    setVideoUp(null);
  };

  // Put the attached incident report into the AP reply: its paragraph into the body (a fresh
  // reply is started around it if there's no draft yet) and its photos ticked to attach.
  const addIncidentToReply = (inc) => {
    if (!inc || !inc.replyText) return;
    setApDraft((cur) => {
      if (cur && cur.includes(inc.replyText)) return cur;
      if (!cur.trim()) return `Hello Team,\n\n${inc.replyText}${mySender?.name ? `\n\nKind Regards,\n${mySender.name}` : ''}`;
      const m = cur.match(/\n\s*Kind Regards,[\s\S]*$/i);
      return m ? `${cur.slice(0, m.index).trimEnd()}\n\n${inc.replyText}${cur.slice(m.index)}` : `${cur.trimEnd()}\n\n${inc.replyText}`;
    });
    setAttachRefs((l) => [...new Set([...l, ...(inc.photos || []).map((p) => p.ref)])]);
    if (inc.email && inc.email.sentAt) setAttachCarryover(true);
    notify?.('Incident report added to the reply — check it below', 'success');
  };

  const draftAp = async (mode = 'brief') => {
    setDrafting(true);
    const res = await api('draftApReply', { id: drawer.id, mode });
    setDrafting(false);
    if (res.success) setApDraft(res.draft);
    else notify?.(res.error || 'Draft failed', 'error');
  };
  const copyDraft = async () => {
    try { await navigator.clipboard.writeText(apDraft); notify?.('Reply copied — paste it into your email to AP', 'success'); }
    catch { notify?.('Copy failed — select the text and copy manually', 'error'); }
  };
  const sendAp = async () => {
    if (!apDraft.trim()) { notify?.('Draft the reply first', 'error'); return; }
    // Fold in any address still sitting in the input boxes (not yet turned into a chip),
    // so a typed-but-un-Entered recipient still counts.
    const pendTo = newToAddr.trim(), pendCc = newAddr.trim();
    const finalTo = [...toList, ...(/@/.test(pendTo) && !toList.includes(pendTo) ? [pendTo] : [])];
    const finalCc = [...ccList, ...(/@/.test(pendCc) && !ccList.includes(pendCc) && !finalTo.includes(pendCc) ? [pendCc] : [])];
    if (!finalTo.length) { notify?.('Add at least one recipient', 'error'); return; }
    setToList(finalTo); setCcList(finalCc); setNewToAddr(''); setNewAddr('');
    const recips = [...finalTo, ...finalCc].join(', ');
    const nImg = attachRefs.length + attachFiles.length;
    const withEml = attachCarryover && incident?.email?.sentAt;
    if (videoUp) { notify?.('Wait for the video to finish uploading', 'error'); return; }
    const nVid = attachVideos.length;
    if (!window.confirm(`Send this reply from ${mySender?.email || 'the shared mailbox'} to:\n\n${recips}\n${nImg ? `\nWith ${nImg} image${nImg > 1 ? 's' : ''} attached.\n` : ''}${nVid ? `\nWith ${nVid > 1 ? `${nVid} video links` : 'a video link'} (60 days) above the sign-off.\n` : ''}${withEml ? `\nWith the carry-over email of ${fmtDate(incident.date)} attached.\n` : ''}\nProceed?`)) return;
    setSending(true);
    const res = await api('sendApReply', {
      id: drawer.id, to: finalTo, cc: finalCc, subject: replySubject, body: apDraft, resolve: resolveOnSend,
      images: attachRefs,
      uploads: attachFiles.map(({ name, contentType, base64 }) => ({ name, contentType, base64 })),
      attachCarryover: !!withEml,
      videos: attachVideos.map(({ path, duration }) => ({ path, duration })),
    });
    setSending(false);
    if (res.success) {
      const nI = (res.attached || 0) - (res.carryover ? 1 : 0);
      const withImgs = [nI ? `${nI} image${nI > 1 ? 's' : ''}` : '', res.videos ? `${res.videos > 1 ? `${res.videos} video links` : 'a video link'}` : '', res.carryover ? 'the carry-over email' : ''].filter(Boolean).join(' and ');
      notify?.(res.awaitingFault
        ? `Sent from ${res.from}${withImgs ? ` with ${withImgs}` : ''} — the case stays in triage until the fault call is recorded`
        : `Sent to Australia Post from ${res.from}${withImgs ? ` with ${withImgs}` : ''}`, res.awaitingFault ? 'info' : 'success');
      setAttachRefs([]); setAttachFiles([]); setAttachVideos([]); setAttachCarryover(false);
      onSaved();
    }
    else notify?.(res.error || 'Send failed', 'error');
  };
  const addAddr = () => { const a = newAddr.trim(); if (/@/.test(a) && !toList.includes(a) && !ccList.includes(a)) { setCcList((l) => [...l, a]); setNewAddr(''); } };
  const addTo = () => { const a = newToAddr.trim(); if (/@/.test(a) && !toList.includes(a) && !ccList.includes(a)) { setToList((l) => [...l, a]); setNewToAddr(''); } };

  // Record the office's fault call (and, for "Close — resolved", resolve the case).
  const saveFault = async (fault, reason, resolve) => {
    setBusy(true);
    const fields = { fault };
    if (reason !== undefined) fields.fault_reason = reason;
    const payload = { id: drawer.id, fields };
    if (resolve) payload.status = 'resolved';
    const res = await api('updateCase', payload);
    setBusy(false);
    if (res.success) {
      setRow((r) => r && ({ ...r, fault, fault_reason: reason !== undefined ? reason : (r.fault_reason || ''), ...(resolve ? { status: 'resolved' } : {}) }));
      if (resolve) { notify?.('Marked resolved', 'success'); onSaved(); }
      else if (reason) notify?.('Fault reason saved', 'success');
    } else notify?.(res.error || 'Failed', 'error');
  };

  const addNote = async () => {
    const message = noteText.trim();
    if (!message) return;
    setBusy(true);
    const res = await api('addNote', { id: drawer.id, message });
    setBusy(false);
    if (res.success) { setNotes((n) => [...n, res.note]); setNoteText(''); }
    else notify?.(res.error || 'Failed', 'error');
  };
  const removeNote = async (noteId) => {
    setBusy(true);
    const res = await api('deleteNote', { id: drawer.id, noteId });
    setBusy(false);
    if (res.success) setNotes((n) => n.filter((x) => x.id !== noteId));
    else notify?.(res.error || 'Failed', 'error');
  };

  // One box, two jobs, and they are not the same thing: writing TO the driver (kind 'admin' —
  // goes in the thread they read and alerts their phone) versus typing up words they already
  // gave you elsewhere (kind 'answer' — a record, deliberately silent). It used to always send
  // 'answer', so a question typed here reached nobody.
  const logResponse = async () => {
    if (!respText.trim()) return;
    setBusy(true);
    const res = await api('addResponse', {
      id: drawer.id,
      kind: logOnly ? 'answer' : 'admin',
      driver_login: row?.driver_login || row?.ap_driver_login || '',
      message: respText.trim(),
      audience: helperLive ? msgTo : 'driver',
    });
    setBusy(false);
    if (res.success) {
      setRespText('');
      // Say who was actually reached, per person. "Sent" on its own is the failure that
      // matters here: a question nobody's phone rang about is a question nobody answers.
      const said = [];
      if (res.driverNotified) {
        const who = row?.driver_login || 'the driver';
        said.push(res.alerted ? `${who}'s phone alerted` : res.smsSent ? `${who} texted` : `${who} NOT reached`);
      }
      if (res.assistLogin) {
        said.push(res.assistAlerted ? `${res.assistLogin}'s phone alerted` : res.assistSms ? `${res.assistLogin} texted` : `${res.assistLogin} NOT reached`);
      }
      const allReached = !said.some((t) => /NOT reached/.test(t));
      notify?.(logOnly ? 'Answer logged'
        : allReached ? `Sent — ${said.join(' · ')}`
        : `Sent — ${said.join(' · ')}. Tell them another way.`,
        logOnly || allReached ? 'success' : 'error');
      const r = await api('getCase', { id: drawer.id });
      if (r.success) { setResponses(r.responses || []); setNotes(r.notes || []); setRow(r.case); }
    }
    else notify?.(res.error || 'Failed', 'error');
  };

  // Take the address back off the Pre-Card register. Admin-only, and the backend clears
  // rather than deletes so the register keeps the history of a point that was once unsafe.
  // Standing requests raised off THIS case, with whether the driver has acknowledged each.
  // Re-read after raising or retiring one rather than patched in place: the ack lands from
  // the driver's phone, not from here, so the server is the only thing that knows.
  const loadRequests = useCallback(async () => {
    if (!drawer.id) return;
    const res = await api('deliveryRequests', { caseId: drawer.id });
    if (res.success) setDeliveryRequests(res.requests || []);
  }, [drawer.id]);

  const clearPrecard = async () => {
    if (!precard) return;
    if (!window.confirm(`Take ${precard.address} off the Pre-Card register?\n\nDrivers stop seeing the warning and it goes back to normal delivery.`)) return;
    setBusy(true);
    const res = await api('removePrecardPoint', { id: drawer.id, pointId: precard.id });
    setBusy(false);
    if (res.success) {
      notify?.('Cleared from the Pre-Card register', 'success');
      setPrecard(null);
      const r = await api('getCase', { id: drawer.id });
      if (r.success) { setResponses(r.responses || []); setNotes(r.notes || []); }
    } else notify?.(res.error || 'Failed', 'error');
  };

  // Re-read the case after an address goes ON the register, so the banner appears without
  // closing and reopening the drawer.
  const reloadPrecard = async () => {
    const r = await api('getCase', { id: drawer.id });
    if (r.success) { setPrecard(r.precard || null); setResponses(r.responses || []); setNotes(r.notes || []); }
  };

  const del = async () => {
    if (!window.confirm('Delete this case permanently?')) return;
    setBusy(true);
    const res = await api('deleteCase', { id: drawer.id });
    setBusy(false);
    if (res.success) { notify?.('Deleted', 'success'); onSaved(); }
    else notify?.(res.error || 'Delete failed', 'error');
  };

  const c = row ? cat(row.case_category) : cat(fields.case_category);
  const matched = row && row.match_status === 'matched';
  // A recovery is live: there are two drivers on this case, and the message box has to ask
  // which of them it is for.
  const helperLogin = (row && row.assist_status === 'open' && row.assist_login) ? String(row.assist_login).toUpperCase() : '';
  const helperLive = !!helperLogin && row.status === 'open';
  // Resolved (or closed) with no At fault / Not at fault call recorded — half-finished.
  // Mirrors faultOutstanding() in customer-cases-admin-sb.js.
  const faultDue = !!row && matched && !((row.fault || '').toString().trim())
    && ['resolved', 'closed'].includes(row.status);

  // The delivery date is the pivot of every disputed-delivery case. Surface it prominently,
  // from the most reliable source available: the actual "Delivered" scan, else the pushed
  // scan snapshot, else the lodgement date.
  // AP flagged the captured signature name as not a real person (structured event, else a fact).
  const signatureInvalid = row && ((row.event_timeline || []).some((e) => /invalid\s*signature/i.test(e.desc || '')) || /invalid\s*signature/i.test([...(row.key_facts || []), row.description || ''].join(' ')));
  const deliveryInfo = (() => {
    if (!row) return null;
    const fmt = (d, withTime) => d.toLocaleString('en-AU', withTime
      ? { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }
      : { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    // An onboard scan is not a delivery — `/deliver/i` used to call it one, which on an RTS
    // case reads as "too late" to whoever is deciding what to tell the driver (utils/caseScan).
    const st = scanState(row.event_timeline);
    if (st && st.key === 'delivered') return { label: 'Delivered', text: fmt(st.at, true) };
    if (st && st.key === 'onboard') return { label: 'Onboard with driver', text: fmt(st.at, true), onboard: true };
    if (row.scan_snapshot && toDate(row.scan_snapshot.date)) return { label: 'Delivered', text: fmt(toDate(row.scan_snapshot.date), false) };
    if (row.date_posted && toDate(row.date_posted)) return { label: 'Posted', text: fmt(toDate(row.date_posted), false) };
    return null;
  })();

  // Business-hours clock — a live tick so it updates while the drawer is open.
  const [clockNow, setClockNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setClockNow(Date.now()), 30000); return () => clearInterval(iv); }, []);
  const receivedMs = row ? new Date(row.created_date || row.created_at).getTime() : NaN;
  const clockEnd = (row && row.status === 'open') ? clockNow : (row && row.updated_at ? new Date(row.updated_at).getTime() : clockNow);
  const workedMs = !isNaN(receivedMs) ? businessMsBetween(receivedMs, clockEnd) : null;
  const clockRunning = row && row.status === 'open' && inBusinessHours(new Date(clockNow));

  return (
    <div style={{ position: 'fixed', inset: 0, width: '100%', maxWidth: '100vw', background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      {/* On a phone the drawer is the whole screen — a 600px panel used to hang off the
          right edge, taking the close button with it. The overlay also has to sit above
          the mobile top bar (z 200), which used to cover the header and its ✕. */}
      <div className="card" style={{ width: 'min(600px, 100vw)', maxWidth: '100vw', height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 0, padding: 20, paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }} onClick={(e) => e.stopPropagation()}>
        {/* Sticky header: triage runs long, and the ✕ has to stay reachable without
            scrolling back to the top. */}
        <div style={{ position: 'sticky', top: -20, zIndex: 2, background: 'var(--hp-card)', margin: '-20px -20px 16px', padding: '20px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: 0, flexWrap: 'wrap', minWidth: 0, flex: '1 1 auto', overflowWrap: 'anywhere' }}>
            <Headphones size={18} style={{ color: 'var(--hp-gold)', alignSelf: 'center' }} />
            {drawer.mode === 'add' ? 'Add customer case' : `Case ${row?.case_number || '—'}`}
            {drawer.mode !== 'add' && row && (row.created_date || row.created_at) && (
              <span style={{ fontSize: '.95rem', fontWeight: 600, color: 'var(--hp-text)', textTransform: 'none' }}>· received from AP {fmtDateTime(row.created_date || row.created_at)}</span>
            )}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close" title="Close"
            style={{ flex: '0 0 auto', minWidth: 40, minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><X size={18} /></button>
        </div>

        {drawer.mode === 'view' && !row ? (
          <div style={{ color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
        ) : (
          <>
            {drawer.mode === 'view' && !edit && row && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
                {/* Answered and resolved, but not yet classified — the case is still in triage. */}
                {faultDue && (
                  <div style={{ border: '1px solid #d97706', borderLeft: '4px solid #d97706', borderRadius: 8, padding: 12, background: 'rgba(217,119,6,0.08)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: '.88rem', color: '#b45309' }}>
                      <AlertTriangle size={14} /> Fault call still to be made
                    </div>
                    <div style={{ fontSize: '.82rem', color: 'var(--hp-text)', marginTop: 5 }}>
                      This case is marked {row.status}, but nobody has recorded whether {row.driver_login || 'the driver'} was
                      at fault. It stays in triage — and out of the driver's stats — until that's done
                      {isAdmin ? ' — the panel is below.' : '. The office will make the call.'}
                    </div>
                  </div>
                )}
                {/* Is the driver actually looking at anything? — answered at the top of the case,
                    before the reader scrolls past three panels of AP paperwork to find out. The
                    driver's portal only shows a case that is confirmed to them AND still open, so
                    a note pushed to an unconfirmed or closed case is a note nobody is reading. */}
                <DriverPushBanner row={row} matched={matched} photoCount={driverPhotos.length} seen={driverSeen} onPreview={() => setPortalPreview(true)} />

                {/* A case at an address we have already ruled unsafe reads FIRST — above
                    Australia Post's own summary — or it gets answered as a first complaint. */}
                <PrecardBanner precard={precard} onClear={clearPrecard} isAdmin={isAdmin} busy={busy} />

                {/* Summary banner */}
                <div style={{ border: `1px solid ${c.color}`, borderLeft: `4px solid ${c.color}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Badge text={c.label} color={c.color} />
                    {row.customer_role && <span style={{ fontSize: '.74rem', color: 'var(--hp-dim)', textTransform: 'capitalize' }}>{row.customer_role}</span>}
                    {row.status === 'open' && <DueChip due_at={row.due_at} force />}
                    {row.signature_required && <span style={{ fontSize: '.7rem', color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: 3 }}><FileText size={11} /> Signature required</span>}
                    {signatureInvalid && <span style={{ fontSize: '.7rem', fontWeight: 700, color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 5, padding: '2px 6px' }}><AlertTriangle size={11} /> AP: invalid signature name</span>}
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {deliveryInfo && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.8rem', fontWeight: 700, color: deliveryInfo.onboard ? '#15803d' : '#b45309', background: deliveryInfo.onboard ? 'rgba(22,163,74,0.12)' : 'rgba(245,158,11,0.12)', border: `1px solid ${deliveryInfo.onboard ? 'rgba(22,163,74,0.4)' : 'rgba(245,158,11,0.4)'}`, borderRadius: 6, padding: '3px 9px' }}>
                          <Calendar size={13} /> {deliveryInfo.label} {deliveryInfo.text}
                        </span>
                      )}
                      {workedMs != null && (
                        <span title={`Working time since received — counts Mon–Fri 9am–5pm only. ${clockRunning ? 'Clock running.' : 'Paused — outside business hours.'}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.8rem', fontWeight: 700, color: 'var(--hp-text)', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6, padding: '3px 9px' }}>
                          <Clock size={13} /> {fmtWorked(workedMs)}
                          <span style={{ width: 7, height: 7, borderRadius: 4, background: clockRunning ? '#16a34a' : 'var(--hp-dim)' }} />
                          <span style={{ fontWeight: 400, color: 'var(--hp-dim)', fontSize: '.72rem' }}>{clockRunning ? 'open' : 'paused'}</span>
                        </span>
                      )}
                    </span>
                  </div>
                  <div style={{ fontSize: '.95rem', fontWeight: 700, marginTop: 8, color: 'var(--hp-text)' }}>{row.summary || row.enquiry_sub_type || '—'}</div>
                  {(row.addressee_address || row.customer_address) && (
                    <div style={{ marginTop: 8, padding: '7px 10px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <MapPin size={14} style={{ color: precard ? PRECARD_RED : 'var(--hp-gold)', flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <span style={{ fontSize: '.68rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)', marginRight: 8 }}>Deliver to</span>
                          <span style={{ fontSize: '.9rem', fontWeight: 700 }}>{cleanAddress(row.addressee_address || row.customer_address)}</span>
                        </div>
                      </div>
                      {/* The barcode, next to the address rather than buried in the scan details
                          below: an instruction like "stop this one and RTS it" is unactionable
                          without it, and whoever writes that instruction is reading this panel. */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, paddingLeft: 20 }}>
                        <span style={{ fontSize: '.68rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)' }}>Article</span>
                        {row.tracking_no
                          ? <span style={{ fontSize: '.84rem', fontWeight: 700, fontFamily: 'var(--font-mono)', userSelect: 'all', wordBreak: 'break-all' }}>{row.tracking_no}</span>
                          : <span style={{ fontSize: '.78rem', fontWeight: 700, color: '#b45309' }}>none on this case — the driver has no barcode to work from</span>}
                      </div>
                      {/* The moment the office learns an address is dangerous is while they are
                          reading the complaint about it — so the register is one click from here. */}
                      {!precard && (
                        <div style={{ marginTop: 6 }}>
                          <AddPrecardBox caseId={drawer.id} address={cleanAddress(row.addressee_address || row.customer_address)}
                            onAdded={reloadPrecard} notify={notify} />
                        </div>
                      )}
                    </div>
                  )}
                  {row.customer_message && (
                    <div style={{ marginTop: 8, padding: 10, background: 'rgba(220,38,38,.06)', borderRadius: 6, fontSize: '.85rem', fontStyle: 'italic', color: 'var(--hp-text)' }}>
                      “{row.customer_message}”
                    </div>
                  )}
                </div>

                {/* Driver confirm */}
                <div style={{ border: `1px solid ${matched ? 'var(--hp-border)' : '#d97706'}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><User size={12} /> Driver</div>
                  {matched ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.95rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{row.driver_login}</span>
                      <span style={{ fontSize: '.72rem', color: '#16a34a' }}>confirmed · {row.released_at ? `pushed to driver ${fmtDateTime(row.released_at)}` : 'released to portal (Phase 2)'}</span>
                      {/* On a phone the driver picker drops to its own line rather than off the edge. */}
                      <DriverPicker drivers={drivers} value={assignSel} onChange={setAssignSel}
                        style={{ minWidth: 0, maxWidth: '100%', flex: '1 1 220px', marginLeft: 'auto' }} />
                      {/* The picker starts on the current driver, so Change only lights up once a
                          different one is picked — pressing it first used to re-save the same
                          driver and close the case, which read as "Change does nothing". */}
                      {(() => {
                        const same = !assignSel || assignSel === (row.driver_login || '').toUpperCase();
                        return (
                          <button className={same ? 'btn btn-ghost btn-sm' : 'btn btn-primary btn-sm'} disabled={busy || same}
                            title={same ? 'Pick a different driver in the box first' : undefined}
                            onClick={() => confirmDriver()}>
                            {same ? 'Change' : `Change to ${assignSel}`}
                          </button>
                        );
                      })()}
                      {assignSel === (row.driver_login || '').toUpperCase() && (
                        <span style={{ flexBasis: '100%', fontSize: '.72rem', color: 'var(--hp-dim)' }}>
                          To change the driver, type their login or name in the box, pick them, then press Change.
                        </span>
                      )}
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                        {row.ap_driver_login
                          ? <>AP named <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-text)' }}>{row.ap_driver_login}</span>{row.match_status === 'unmatched' && <span style={{ color: '#d97706' }}> — not an active driver login, please pick</span>}</>
                          : 'AP gave no driver login — pick the driver.'}
                      </div>
                      {row.match_status === 'suggested' && suggestion(row).suggested_driver && (
                        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginBottom: 8 }} disabled={busy} onClick={() => confirmDriver(suggestion(row).suggested_driver)}>
                          <Check size={15} /> Confirm {suggestion(row).suggested_driver}
                        </button>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <DriverPicker drivers={drivers} value={assignSel} onChange={setAssignSel}
                          placeholder="Choose a driver — type a login or name…" style={{ minWidth: 0, flex: '1 1 220px' }} />
                        <button className="btn btn-secondary" disabled={busy} onClick={() => confirmDriver()}>Confirm</button>
                      </div>
                    </>
                  )}
                </div>

                {/* Recovery — hand the legwork to a second driver */}
                {row.status === 'open' && (
                  <div style={{ border: `1px solid ${row.assist_status === 'open' ? '#2563eb' : 'var(--hp-border)'}`, borderRadius: 8, padding: 12 }}>
                    <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><UserPlus size={12} /> Recovery — another driver</div>
                    {row.assist_status === 'open' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.9rem', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{row.assist_login}</span>
                          <span style={{ fontSize: '.72rem', color: '#2563eb' }}>
                            asked to recover{row.assist_pushed_at ? ` · sent ${fmtDateTime(row.assist_pushed_at)}` : ''}
                          </span>
                          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} disabled={busy} onClick={cancelAssist}>Stand down</button>
                        </div>
                        {row.assist_task && <div style={{ fontSize: '.84rem', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6, padding: '8px 10px' }}>{row.assist_task}</div>}
                        <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)' }}>
                          It's on their portal now (Customer Service). The case stays {row.driver_login || 'the named driver'}'s — this doesn't move the complaint or the at-fault call.
                        </div>
                      </div>
                    ) : !assistOpen ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>
                          {row.assist_status === 'done'
                            ? `${row.assist_login} reported back${row.assist_done_at ? ` ${fmtDateTime(row.assist_done_at)}` : ''}${row.assist_pushed_at ? ` (sent ${fmtDateTime(row.assist_pushed_at)})` : ''} — send it to someone else if it still needs a look.`
                            : "Driver can't get there — on leave, off the round? Send the search to whoever is at that address today."}
                        </span>
                        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => { setAssistOpen(true); if (!assistTask.trim()) setAssistTask(defaultAssistTask(row)); }}>
                          <UserPlus size={14} /> Send to another driver
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div>
                          <label style={{ ...labelStyle, color: assistSel ? 'var(--hp-gold)' : '#d97706' }}>
                            {assistSel ? `Recovery driver · ${assistSel}` : 'Step 1 — pick the driver'}
                          </label>
                          <div style={{ borderRadius: 8, outline: assistSel ? undefined : '1px solid #d97706' }}>
                            <DriverPicker drivers={drivers.filter((d) => d.login !== (row.driver_login || '').toUpperCase())}
                              value={assistSel} onChange={(v) => { setAssistSel(v); setAssistErr(''); }}
                              placeholder="Who will go and look? Type a login or name…" />
                          </div>
                        </div>
                        <div>
                          <label style={labelStyle}>Step 2 — what they should do</label>
                          <textarea className="input" style={{ ...inputStyle, minHeight: 78, fontFamily: 'inherit' }} value={assistTask}
                            onChange={(e) => setAssistTask(e.target.value)} placeholder="What do you want them to do at the address?" />
                        </div>
                        {assistErr && <div style={{ fontSize: '.82rem', color: '#dc2626' }}>{assistErr}</div>}
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-primary" disabled={busy} onClick={sendAssist}>
                            <Send size={14} /> {busy ? 'Sending…' : 'Send & text them'}
                          </button>
                          <button className="btn btn-ghost" disabled={busy} onClick={() => { setAssistOpen(false); setAssistErr(''); }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <DriverShareBox caseId={row.id} share={driverShare} row={row}
                  imageCount={imageUrls.length || (row.case_images || []).length}
                  notify={notify} onSaved={setDriverShare} />

                <DriverNoteBuilder
                  caseId={row.id}
                  initial={row.driver_note || ''}
                  pushedAt={row.driver_note_at || null}
                  onPushed={(patch) => setRow((r) => ({ ...r, ...patch }))}
                  sources={[
                    { label: "Customer's words", text: row.customer_message || '' },
                    { label: 'Summary', text: row.summary || '' },
                    { label: 'Issue details', text: row.description || '' },
                    ...(row.key_facts || []).map((f) => ({ label: 'Key fact', text: (f || '').toString() })),
                  ]}
                  notify={notify}
                />

                <QuestionnaireBuilder caseId={row.id} initial={row.questionnaire} pushedAt={row.questionnaire_at || null}
                  hasPhoto={imageUrls.length > 0 || (row.case_images || []).length > 0} answered={!!row.questionnaire_answers}
                  notify={notify} onPushed={(patch) => setRow((r) => ({ ...r, ...patch }))} />

                {matched && (
                  <DriverPhotoBox caseId={row.id} photos={driverPhotos} driver={row.driver_login || ''}
                    helper={helperLive ? helperLogin : ''} open={row.status === 'open'}
                    notify={notify} onChanged={setDriverPhotos} />
                )}

                {/* An Authority to Leave AP approved on this case → Standing Orders, with the
                    case as its evidence. Admin-only (the register is). */}
                {isAdmin && (row.addressee_address || row.customer_address) && (
                  <AtlFromCaseBox row={row} address={cleanAddress(row.addressee_address || row.customer_address)}
                    apText={[row.summary, row.customer_message, ...responses.filter((r) => r.via === 'ap').map((r) => r.message)].join(' ')}
                    notify={notify} />
                )}
                {isAdmin && (row.addressee_address || row.customer_address) && (
                  <CareFromCaseBox row={row} address={cleanAddress(row.addressee_address || row.customer_address)} notify={notify} />
                )}

                {/* Resolving a case files it. What the customer asked us to do from now on
                    has to outlive that, or it goes into a Resolved list nobody opens. */}
                {matched && (
                  <DeliveryRequestBox caseId={row.id} driver={row.driver_login || ''}
                    address={cleanAddress(row.addressee_address || row.customer_address)}
                    seed={(row.driver_note || row.summary || '').toString()}
                    hasCasePhoto={(row.case_images || []).length > 0}
                    requests={deliveryRequests} notify={notify} onChanged={loadRequests} />
                )}

                {/* Driver's answer — the thing you're here to act on. An AP message logged on
                    the thread is not an answer: it still leaves the driver's word owed. */}
                <div style={{ border: `1px solid ${responses.some(r => r.via !== 'admin' && r.via !== 'ap') ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><MessageSquare size={12} /> Driver's answer</div>
                  {!responses.some(r => r.via !== 'admin' && r.via !== 'ap') && (
                    <div style={{ fontSize: '.84rem', color: matched && row.status === 'open' ? '#d97706' : 'var(--hp-dim)', marginBottom: responses.length ? 8 : 0 }}>
                      {!matched
                        ? 'Confirm a driver above to send this case to their portal.'
                        : row.status !== 'open'
                          ? `${row.driver_login} never wrote an answer here — the case was ${row.status}${row.questionnaire_answers ? ' after they answered the questions above' : ''}.`
                          : `Awaiting ${row.driver_login}'s answer — the case is in their portal.`}
                    </div>
                  )}
                  {responses.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {responses.map((r) => (
                        <div key={r.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 10, background: r.via === 'admin' ? 'transparent' : 'rgba(212,175,55,0.06)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.72rem', color: 'var(--hp-dim)' }}>
                            <span style={{ fontWeight: 700, color: 'var(--hp-text)' }}>{r.via === 'admin' ? 'Office' : r.via === 'ap' ? 'Australia Post' : (r.driver_login || 'Driver')}</span>
                            <span style={{ marginLeft: 'auto' }}>{fmtDateTime(r.created_at)}</span>
                          </div>
                          {r.outcome && <div style={{ fontSize: '.9rem', marginTop: 4, fontWeight: 700 }}>{r.outcome}</div>}
                          {r.message && <div style={{ fontSize: '.86rem', marginTop: 2, whiteSpace: 'pre-wrap' }}>{r.message}</div>}
                          {(r.photos || []).length > 0 && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                              {r.photos.map((p, i) => <a key={i} href={p} target="_blank" rel="noreferrer"><img src={p} alt="evidence" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} /></a>)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Two drivers, one thread: the owner answers for the delivery, the helper
                      is out looking for the parcel. Both read everything; this picks whose
                      phone rings. Only offered while a recovery is actually live. */}
                  {helperLive && !logOnly && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)' }}>Alert</span>
                      {[['driver', row.driver_login || 'the driver'], ['assist', `${helperLogin} (recovery)`], ['both', 'Both']].map(([k, lbl]) => (
                        <button key={k} type="button" onClick={() => setMsgTo(k)}
                          style={{ fontSize: '.76rem', fontWeight: 700, padding: '4px 10px', borderRadius: 14, cursor: 'pointer',
                            border: `1px solid ${msgTo === k ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                            background: msgTo === k ? 'rgba(201,168,76,0.15)' : 'transparent', color: 'var(--hp-text)' }}>{lbl}</button>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <input className="input" style={inputStyle} value={respText} onChange={(e) => setRespText(e.target.value)}
                      placeholder={logOnly ? "Type the driver's own words…"
                        : helperLive && msgTo === 'assist' ? `Message ${helperLogin} about the recovery — they're alerted…`
                        : helperLive && msgTo === 'both' ? `Message ${row.driver_login || 'the driver'} and ${helperLogin} — both alerted…`
                        : `Message ${row.driver_login || 'the driver'} — they're alerted…`}
                      onKeyDown={(e) => { if (e.key === 'Enter') logResponse(); }} />
                    <button className="btn btn-secondary" disabled={busy || !respText.trim()} onClick={logResponse}>{logOnly ? 'Log' : 'Send'}</button>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '.74rem', color: 'var(--hp-dim)', cursor: 'pointer' }}
                    title="For an answer the driver already gave you somewhere else (WhatsApp, in person). It goes on the record without telling them anything.">
                    <input type="checkbox" checked={logOnly} onChange={(e) => setLogOnly(e.target.checked)} />
                    These are the driver's own words — record them, don't send
                  </label>
                </div>

                {/* Delivery photo(s) from the case. An AP PDF often carries several — the
                    safe-drop shot, a second angle, a scan of nothing much — and which one
                    goes to the driver and onto the reply is a judgement, so it's a choice
                    here rather than "all of them". Setting one aside never deletes it. */}
                {imagePool.length > 0 && (
                  <div>
                    <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <Camera size={12} /> Delivery photo{imagePool.length > 1 ? 's' : ''}
                      {imagePool.length > 1 && (
                        <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto', textTransform: 'none' }}
                          onClick={() => setChoosing((v) => !v)}>{choosing ? 'Done' : 'Choose photos'}</button>
                      )}
                    </div>
                    {choosing && (
                      <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                        Tick the ones that belong on this case — they're what the driver sees and what
                        you can attach to AP. The first is the main photo. Nothing is deleted.
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {imagePool.filter((p) => choosing || p.chosen).map((p, i) => (
                        <div key={p.path} style={{ position: 'relative' }}>
                          {choosing ? (
                            <img src={p.url} alt="delivery" onClick={() => toggleImage(p.path)}
                              style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', opacity: p.chosen ? 1 : .35, border: `2px solid ${p.chosen ? 'var(--hp-gold)' : 'var(--hp-border)'}` }} />
                          ) : (
                            <a href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt="delivery" style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)' }} /></a>
                          )}
                          {choosing && p.chosen && (
                            <span style={{ position: 'absolute', top: 4, left: 4, background: 'var(--hp-gold)', color: '#000', borderRadius: 4, fontSize: '.66rem', fontWeight: 700, padding: '1px 5px' }}>
                              {chosenPaths.indexOf(p.path) === 0 ? 'MAIN' : `#${chosenPaths.indexOf(p.path) + 1}`}
                            </span>
                          )}
                          {choosing && p.chosen && chosenPaths.indexOf(p.path) > 0 && (
                            <button type="button" className="btn btn-ghost btn-sm" disabled={busy}
                              onClick={() => makeMainImage(p.path)}
                              style={{ position: 'absolute', bottom: 4, left: 4, fontSize: '.66rem', padding: '1px 6px', background: 'var(--hp-card)' }}>Make main</button>
                          )}
                        </div>
                      ))}
                      {!chosenPaths.length && (
                        <div style={{ fontSize: '.78rem', color: '#b45309', alignSelf: 'center' }}>
                          {choosing ? "No photo on the case — the driver's card will show none."
                            : `${imagePool.length} photo${imagePool.length > 1 ? 's' : ''} set aside — none on the case. Choose photos to put one back.`}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isAdmin ? (
                <div style={{ border: `1px solid ${faultDue ? '#d97706' : 'var(--hp-border)'}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={12} /> Is the driver at fault?</div>
                  {faultDue && <div style={{ fontSize: '.78rem', color: '#b45309', marginBottom: 8 }}>Outstanding — record it to take this case out of triage.</div>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['no', 'Not at fault', '#16a34a'], ['yes', 'At fault', '#b91c1c']].map(([val, lbl, col]) => (
                      <button key={val} disabled={busy} onClick={() => { setOtherOpen(false); saveFault(val, ''); }}
                        style={{ flex: 1, padding: '9px 10px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '.85rem', border: `1px solid ${row.fault === val ? col : 'var(--hp-border)'}`, background: row.fault === val ? col : 'transparent', color: row.fault === val ? '#fff' : 'var(--hp-text)' }}>{lbl}</button>
                    ))}
                  </div>
                  {row.fault && (() => {
                    const opts = row.fault === 'no' ? FAULT_NO : FAULT_YES;
                    const isOther = !!row.fault_reason && !opts.includes(row.fault_reason);
                    const showOther = otherOpen || isOther;
                    const selVal = showOther ? 'Other — specify' : (opts.includes(row.fault_reason) ? row.fault_reason : '');
                    return (
                      <div style={{ marginTop: 10 }}>
                        <select className="input" style={inputStyle} value={selVal} onChange={(e) => {
                          const v = e.target.value;
                          if (!v) return;
                          if (v === 'Other — specify') { setOtherOpen(true); }
                          else if (v === 'Close the case — resolved') { setOtherOpen(false); saveFault('no', 'Resolved', true); }
                          else { setOtherOpen(false); saveFault(row.fault, v); }
                        }}>
                          <option value="">Choose a reason…</option>
                          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                        {showOther && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <input className="input" style={inputStyle} value={specify} onChange={(e) => setSpecify(e.target.value)} placeholder="Specify the reason…" />
                            <button className="btn btn-secondary" disabled={busy || !specify.trim() || specify.trim() === row.fault_reason} onClick={() => { saveFault(row.fault, specify.trim()); setOtherOpen(false); }}>{specify.trim() && specify.trim() === row.fault_reason ? 'Saved' : 'Save'}</button>
                          </div>
                        )}
                        {/* A typed reason keeps its box open (so it can be edited), so the
                            Recorded line shows whenever what's in the box is what's stored —
                            otherwise a saved "Other" reason looked as if it never saved. */}
                        {row.fault_reason && (!showOther || specify.trim() === row.fault_reason) && (
                          <div style={{ fontSize: '.78rem', color: '#16a34a', marginTop: 6 }}>Recorded: {row.fault === 'yes' ? 'At fault' : 'Not at fault'} — {row.fault_reason}</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                ) : (
                /* A team leader sees the fault call, never makes it. */
                <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={12} /> Is the driver at fault?</div>
                  {row.fault
                    ? <div style={{ fontSize: '.82rem', color: row.fault === 'yes' ? '#b91c1c' : '#16a34a', fontWeight: 700 }}>
                        {row.fault === 'yes' ? 'At fault' : 'Not at fault'}{row.fault_reason ? ` — ${row.fault_reason}` : ''}
                      </div>
                    : <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>Not decided yet.</div>}
                  <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                    The fault decision is made by the office — answer and resolve the case as normal. It stays in
                    triage until the office records the call, so nothing is missed.
                  </div>
                </div>
                )}
                <IncidentReportBox caseId={drawer.id} row={row} incident={incident} notify={notify}
                  onChanged={async (inc) => {
                    setIncident(inc);
                    // Attach / detach / alert each leave a line on the case (and the alert can
                    // confirm the driver and move the stage) — reload the drawer in place.
                    const r = await api('getCase', { id: drawer.id });
                    if (r.success) {
                      setRow(r.case); setNotes(r.notes || []); setResponses(r.responses || []); setIncident(r.incident || null);
                    }
                  }}
                  onUseInReply={addIncidentToReply} />

                {/* Internal case notes — record-keeping only, nothing leaves the office */}
                <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><StickyNote size={12} /> Case notes</div>
                  <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginBottom: 8 }}>Internal only — never sent to the driver or Australia Post.</div>
                  {notes.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                      {notes.map((n) => (
                        <div key={n.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '8px 10px', background: 'rgba(212,175,55,0.05)' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', gap: 8, fontSize: '.72rem', color: 'var(--hp-dim)' }}>
                              <span style={{ fontWeight: 700, color: 'var(--hp-text)', fontFamily: 'var(--font-mono)' }}>{n.author || 'Office'}</span>
                              <span>{fmtDateTime(n.created_at)}</span>
                            </div>
                            <div style={{ fontSize: '.86rem', marginTop: 2, whiteSpace: 'pre-wrap' }}>{n.message}</div>
                          </div>
                          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => removeNote(n.id)} title="Delete this note"><X size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="input" style={inputStyle} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note to the case…" onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} />
                    <button className="btn btn-secondary" disabled={busy || !noteText.trim()} onClick={addNote}>Add note</button>
                  </div>
                </div>

                {/* Draft reply to Australia Post */}
                <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><Send size={12} /> Reply to Australia Post</div>
                  {!apDraft ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button className="btn btn-primary" disabled={drafting} onClick={() => draftAp('brief')}>
                        {drafting ? <><Loader size={14} /> Drafting…</> : <>✍ Draft a brief reply</>}
                      </button>
                      <button className="btn btn-secondary btn-sm" disabled={drafting} onClick={() => draftAp('detailed')}
                        title="AP bounced the case back for more info — draft a fuller reply with the delivery detail">
                        Fuller reply (bounce-back)
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Recipients — reply-all, editable. Review before sending. */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', width: 26 }}>To</span>
                        {toList.map((a) => <Chip key={a} addr={a} onX={() => setToList((l) => l.filter((x) => x !== a))} strong />)}
                        <input className="input" style={{ ...inputStyle, width: 180, padding: '3px 8px', fontSize: '.78rem' }} value={newToAddr} onChange={(e) => setNewToAddr(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTo(); } }} placeholder="add address…" />
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', width: 26 }}>Cc</span>
                        {ccList.map((a) => <Chip key={a} addr={a} onX={() => setCcList((l) => l.filter((x) => x !== a))} />)}
                        <input className="input" style={{ ...inputStyle, width: 180, padding: '3px 8px', fontSize: '.78rem' }} value={newAddr} onChange={(e) => setNewAddr(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAddr(); } }} placeholder="add address…" />
                      </div>
                      <input className="input" style={{ ...inputStyle, marginBottom: 8 }} value={replySubject} onChange={(e) => setReplySubject(e.target.value)} placeholder="Subject" />
                      <textarea style={{ ...inputStyle, width: '100%', minHeight: 150, padding: 10, fontFamily: 'inherit', borderRadius: 8 }} value={apDraft} onChange={(e) => setApDraft(e.target.value)} />

                      {/* Images to send with the reply — tick any photo already on the case, add more from this computer */}
                      <div style={{ marginTop: 10 }}>
                        <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Camera size={12} /> Images{(attachRefs.length + attachFiles.length) > 0 ? ` — ${attachRefs.length + attachFiles.length} attached` : ''}
                        </div>
                        {casePhotos.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            {casePhotos.map((p) => {
                              const on = attachRefs.includes(p.ref);
                              return (
                                <button key={p.ref} type="button" onClick={() => toggleAttach(p.ref)} title={`${on ? 'Remove from' : 'Attach to'} the reply — ${p.who}`}
                                  style={{ position: 'relative', padding: 0, border: `2px solid ${on ? '#16a34a' : 'var(--hp-border)'}`, borderRadius: 6, background: 'none', cursor: 'pointer', lineHeight: 0 }}>
                                  <img src={p.url} alt={p.who} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, opacity: on ? 1 : 0.65 }} />
                                  {on && <span style={{ position: 'absolute', top: -6, right: -6, background: '#16a34a', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={11} /></span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {attachFiles.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            {attachFiles.map((f, i) => (
                              <div key={i} style={{ position: 'relative', lineHeight: 0 }}>
                                <img src={f.preview} alt={f.name} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '2px solid #16a34a' }} />
                                <button type="button" onClick={() => setAttachFiles((l) => l.filter((_, j) => j !== i))} title="Remove"
                                  style={{ position: 'absolute', top: -6, right: -6, background: 'var(--hp-card, #fff)', border: '1px solid var(--hp-border)', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}><X size={11} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                        {scannerShots.length > 0 && (
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                              <ScanLine size={12} /> Scanner screenshots already in the app
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {scannerShots.map((p) => {
                                const on = attachRefs.includes(p.ref);
                                return (
                                  <button key={p.ref} type="button" onClick={() => toggleAttach(p.ref)}
                                    title={`${on ? 'Remove from' : 'Attach to'} the reply — ${p.driver} ${fmtDate(p.date)}${p.note ? ` · ${p.note}` : ''}`}
                                    style={{ position: 'relative', padding: 0, border: `2px solid ${on ? '#16a34a' : 'var(--hp-border)'}`, borderRadius: 6, background: 'none', cursor: 'pointer', lineHeight: 0 }}>
                                    <img src={p.url} alt={`${p.driver} ${p.date}`} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, opacity: on ? 1 : 0.65 }} />
                                    <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '.6rem', lineHeight: 1.4, borderRadius: '0 0 4px 4px' }}>{shortDay(p.date)}</span>
                                    {on && <span style={{ position: 'absolute', top: -6, right: -6, background: '#16a34a', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={11} /></span>}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <UploadCloud size={14} /> Add an image
                          <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                            onChange={(e) => { addAttachFiles(e.target.files); e.target.value = ''; }} />
                        </label>
                        <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginLeft: 8 }}>
                          {casePhotos.length ? 'Tick a photo above to send it, or add one from this computer.' : 'Add a photo from this computer to send it with the reply.'}
                        </span>
                      </div>

                      {/* A short video — uploaded now, sent as a 60-day watch link (too big to attach) */}
                      <div style={{ marginTop: 10 }}>
                        <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Video size={12} /> Video{attachVideos.length ? ` — ${attachVideos.length} added` : ''}
                        </div>
                        {attachVideos.length > 0 && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                            {attachVideos.map((v, i) => (
                              <div key={v.path} style={{ position: 'relative', width: 200 }}>
                                <video src={v.preview} controls playsInline preload="metadata"
                                  style={{ width: '100%', maxHeight: 220, borderRadius: 6, border: '2px solid #16a34a', background: '#000', display: 'block' }} />
                                <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                                  {v.name || `Video ${i + 1}`}{v.duration ? ` · ${fmtDuration(v.duration)}` : ''} · {fmtMB(v.size)}
                                </div>
                                <button type="button" onClick={() => setAttachVideos((l) => l.filter((_, j) => j !== i))} title="Don't send this video"
                                  style={{ position: 'absolute', top: -6, right: -6, background: 'var(--hp-card, #fff)', border: '1px solid var(--hp-border)', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}><X size={11} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                        {videoUp ? (
                          <div style={{ maxWidth: 280 }}>
                            <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginBottom: 3 }}>Uploading {videoUp.name || 'video'}… {Math.round(videoUp.frac * 100)}%</div>
                            <div style={{ height: 6, background: 'var(--hp-border)', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${Math.round(videoUp.frac * 100)}%`, height: '100%', background: 'var(--hp-gold)', transition: 'width .2s' }} />
                            </div>
                          </div>
                        ) : attachVideos.length < 2 && (
                          <>
                            <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Video size={14} /> Add a video
                              <input type="file" accept="video/*" style={{ display: 'none' }}
                                onChange={(e) => { const f = (e.target.files || [])[0]; e.target.value = ''; if (f) addReplyVideo(f); }} />
                            </label>
                            <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginLeft: 8 }}>
                              Up to 50MB (about 30 seconds). Too big to attach, so AP gets a "watch" link that stays open 60 days.
                            </span>
                          </>
                        )}
                      </div>

                      {incident?.email?.sentAt && (
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '.8rem', margin: '10px 0 0', padding: '8px 10px', borderRadius: 8, border: `1px solid ${attachCarryover ? '#16a34a' : 'var(--hp-border)'}` }}>
                          <input type="checkbox" checked={attachCarryover} onChange={(e) => setAttachCarryover(e.target.checked)} style={{ marginTop: 2 }} />
                          <span>
                            <Mail size={12} style={{ verticalAlign: '-2px' }} /> Attach the carry-over email AP received ({fmtDate(incident.date)}, sent {fmtDateTime(incident.email.sentAt)}) as an email file
                            {!incident.emailExact && <span style={{ display: 'block', fontSize: '.72rem', color: 'var(--hp-dim)' }}>Sent before copies were kept, so it's rebuilt from that day's records as they stood at the send time.</span>}
                          </span>
                        </label>
                      )}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem', margin: '8px 0' }}>
                        <input type="checkbox" checked={resolveOnSend} onChange={(e) => setResolveOnSend(e.target.checked)} /> Mark the case resolved after sending
                      </label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button className="btn btn-primary" disabled={sending || !mySender || !!videoUp} onClick={sendAp} style={{ background: '#16a34a', borderColor: '#16a34a' }}>
                          {sending ? <><Loader size={14} /> Sending…</> : <><Send size={14} /> Send to AP</>}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={copyDraft}><FileText size={14} /> Copy</button>
                        <button className="btn btn-ghost btn-sm" disabled={drafting} onClick={() => draftAp('brief')}>{drafting ? 'Re-drafting…' : 'Re-draft brief'}</button>
                        <button className="btn btn-ghost btn-sm" disabled={drafting} onClick={() => draftAp('detailed')} title="Fuller reply for an AP bounce-back">Fuller</button>
                      </div>
                      <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                        {mySender ? <>Sends signed as <strong>{mySender.name}</strong>, replies routed to <strong>{mySender.email}</strong>. Review the recipients — this goes to everyone above.</>
                          : <span style={{ color: '#b91c1c' }}>No reply address is set up for your login — ask Paul to add it before sending.</span>}
                      </div>
                    </>
                  )}
                </div>

                {/* Ask the driver for a photo */}
                {row.status === 'open' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '.86rem', border: `1px solid ${row.photo_requested ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 8, padding: '9px 11px' }}>
                    <input type="checkbox" checked={!!row.photo_requested} disabled={busy} onChange={togglePhoto} />
                    <Camera size={15} style={{ color: row.photo_requested ? 'var(--hp-gold)' : 'var(--hp-dim)' }} />
                    <span>Ask the driver to take a photo{row.photo_requested ? ' — requested' : ''}</span>
                  </label>
                )}

                {/* Send the driver a refresher — brief to My Docs + quiz to the Training tab */}
                {briefs.length > 0 && (
                  <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: '11px 12px' }}>
                    <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <GraduationCap size={12} /> Send the driver a refresher
                    </div>
                    <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                      {row.driver_login
                        ? <>The brief lands in <strong>{row.driver_login}</strong>'s My Docs, the quiz becomes required on their home screen, and they get a text.</>
                        : <>Confirm the case to a driver first — a refresher follows whoever owns it.</>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {briefs.map((b) => {
                        const sent = pushedBriefs.find((p) => p.id === b.id);
                        const hint = suggestedBriefs.includes(b.id);
                        return (
                          <label key={b.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: row.driver_login ? 'pointer' : 'not-allowed', fontSize: '.86rem', border: `1px solid ${sent ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 8, padding: '9px 11px', opacity: row.driver_login ? 1 : 0.6 }}>
                            <input type="checkbox" checked={!!sent} disabled={busy || !row.driver_login || pushingBrief === b.id}
                              onChange={() => toggleBrief(b)} style={{ marginTop: 3 }} />
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontWeight: 700 }}>{b.label}</span>
                              {hint && !sent && <span style={{ marginLeft: 6, fontSize: '.68rem', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--hp-gold)' }}>suggested</span>}
                              {pushingBrief === b.id && <span style={{ marginLeft: 6, fontSize: '.72rem', color: 'var(--hp-dim)' }}>sending…</span>}
                              <span style={{ display: 'block', fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 2, lineHeight: 1.45 }}>{b.blurb}</span>
                              {sent && (
                                <span style={{ display: 'block', fontSize: '.72rem', color: 'var(--hp-green)', marginTop: 4 }}>
                                  Sent {fmtDateTime(sent.at)}{sent.by ? ` by ${sent.by}` : ''}{sent.smsSent ? ' · texted' : ''}
                                </span>
                              )}
                              {b.guideUrl && (
                                <a href={b.guideUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                                  style={{ display: 'inline-block', fontSize: '.72rem', color: 'var(--hp-gold)', marginTop: 4 }}>
                                  Preview the brief →
                                </a>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Case context (tucked away) */}
                <details>
                  <summary style={{ ...labelStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}><ListChecks size={12} /> Case context &amp; scan history</summary>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
                    {(row.key_facts || []).length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: '.85rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {row.key_facts.map((k, i) => <li key={i}>{k}</li>)}
                      </ul>
                    )}
                    {(row.event_timeline || []).length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '.8rem' }}>
                        {row.event_timeline.map((e, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, borderLeft: '2px solid var(--hp-border)', paddingLeft: 8 }}>
                            <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{e.at || ''}</span>
                            <span>{e.desc}{e.location ? ` · ${e.location}` : ''}{e.user ? ` · ${e.user}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: '.78rem', color: 'var(--hp-dim)' }}>
                      <span><Calendar size={11} style={{ verticalAlign: -1 }} /> Received {fmtDateTime(row.created_date)}</span>
                      {apOpened(row) && <span title="AP's own case-created date, off the case detail page">AP opened {fmtDateTime(apOpened(row))}</span>}
                      {row.sla_target_date && <span>SLA {fmtDate(row.sla_target_date)}</span>}
                      {row.tracking_no && <span style={{ fontFamily: 'var(--font-mono)' }}>{row.tracking_no}</span>}
                      {row.contract && <span>{row.contract}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {attachmentUrl && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={openPdf} style={{ alignSelf: 'flex-start' }}>
                          <FileText size={14} /> View original PDF
                        </button>
                      )}
                      {row.tracking_no && (
                        <Link className="btn btn-secondary btn-sm" to={`/scanner-audit?article=${encodeURIComponent(row.tracking_no.trim())}&case=${encodeURIComponent(row.id)}`} style={{ alignSelf: 'flex-start' }}
                          title="Open this article in the Scanner Audit — deliveries before &amp; after, with the self-signature check">
                          <ScanLine size={14} /> Scanner audit
                        </Link>
                      )}
                    </div>
                  </div>
                </details>
              </div>
            )}

            {(edit || drawer.mode === 'add') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
                <label
                  onDragOver={(e) => { e.preventDefault(); setDropOver(true); }}
                  onDragLeave={() => setDropOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDropOver(false); processFile(e.dataTransfer?.files?.[0]); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', border: `2px dashed ${dropOver ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: dropOver ? 'rgba(212,175,55,.08)' : 'transparent', borderRadius: 8, padding: '14px 16px', transition: 'all .12s' }}
                >
                  <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={(e) => { processFile(e.target.files?.[0]); e.target.value = ''; }} />
                  {reading ? <Loader size={18} style={{ color: 'var(--hp-gold)' }} /> : <UploadCloud size={18} style={{ color: 'var(--hp-gold)' }} />}
                  <div style={{ fontSize: '.85rem' }}>
                    {reading ? 'Reading the case…' : file ? <><strong>{file.name}</strong> attached — drop another to replace</> : <>Drag the AP case <strong>PDF</strong> here, or click — it'll draft the questions</>}
                  </div>
                  {file && !reading && <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={(e) => { e.preventDefault(); setFile(null); }}><X size={13} /></button>}
                </label>

                {imgPreviews.length > 0 && (
                  <div>
                    <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Camera size={12} /> {imgPreviews.length} delivery photo{imgPreviews.length > 1 ? 's' : ''} detected
                      {imgPreviews.length > 1 ? ' — click one to leave it off the case' : ' — will be attached & shown to the driver'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {imgPreviews.map((src, i) => (
                        <img key={i} src={src} alt="delivery"
                          onClick={() => setKeepImgs((cur) => { const n = new Set(cur); if (n.has(i)) n.delete(i); else n.add(i); return n; })}
                          style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', opacity: keepImgs.has(i) ? 1 : .35, border: `2px solid ${keepImgs.has(i) ? 'var(--hp-gold)' : 'var(--hp-border)'}` }} />
                      ))}
                    </div>
                    {imgPreviews.length > keepImgs.size && (
                      <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                        {imgPreviews.length - keepImgs.size} set aside — still kept with the case, just not shown to the driver.
                      </div>
                    )}
                  </div>
                )}

                <Row>
                  <Col label="Case number"><input className="input" style={inputStyle} value={fields.case_number} onChange={(e) => setFields({ ...fields, case_number: e.target.value })} /></Col>
                  <Col label="Category">
                    <select className="input" style={inputStyle} value={fields.case_category} onChange={(e) => setFields({ ...fields, case_category: e.target.value })}>
                      {Object.entries(CATEGORY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </Col>
                </Row>
                <Col label="Enquiry sub-type (AP wording)"><input className="input" style={inputStyle} value={fields.enquiry_sub_type} onChange={(e) => setFields({ ...fields, enquiry_sub_type: e.target.value })} /></Col>
                <Col label="Summary (one line)"><input className="input" style={inputStyle} value={fields.summary} onChange={(e) => setFields({ ...fields, summary: e.target.value })} /></Col>
                <Col label="Customer's words (verbatim — office only; Include it in the driver note to send it)"><textarea className="input" style={{ ...inputStyle, minHeight: 50 }} value={fields.customer_message} onChange={(e) => setFields({ ...fields, customer_message: e.target.value })} /></Col>
                <Row>
                  <Col label="Customer name"><input className="input" style={inputStyle} value={fields.customer_name} onChange={(e) => setFields({ ...fields, customer_name: e.target.value })} /></Col>
                  <Col label="Role" flex="0 0 120px">
                    <select className="input" style={inputStyle} value={fields.customer_role} onChange={(e) => setFields({ ...fields, customer_role: e.target.value })}>
                      <option value="receiver">Receiver</option><option value="sender">Sender</option>
                    </select>
                  </Col>
                </Row>
                <Col label="Customer address (who raised the enquiry — office reference)"><input className="input" style={inputStyle} value={fields.customer_address} onChange={(e) => setFields({ ...fields, customer_address: e.target.value })} /></Col>
                {/* THE driver-facing address. Every driver surface reads addressee → customer
                    (_shared/case-driver-view), so correcting only the customer address changes
                    the engine and leaves the driver's card on AP's original — which is exactly
                    what an RTS case needs changed (Paul, 21 Sep 2026, case 73925330). */}
                <Row>
                  <Col label="Deliver to — the address the driver sees">
                    <input className="input" style={inputStyle} value={fields.addressee_address}
                      onChange={(e) => setFields({ ...fields, addressee_address: e.target.value })}
                      placeholder={fields.customer_address ? 'Blank = the driver is shown the customer address above' : ''} />
                  </Col>
                  <Col label="Addressee name" flex="0 0 220px"><input className="input" style={inputStyle} value={fields.addressee_name} onChange={(e) => setFields({ ...fields, addressee_name: e.target.value })} /></Col>
                </Row>
                {fields.addressee_address && fields.customer_address
                  && fields.addressee_address.trim() !== fields.customer_address.trim() && (
                  <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginTop: -4 }}>
                    The driver's card shows <b>{cleanAddress(fields.addressee_address)}</b> — on a return-to-sender put the
                    return address here, or the driver is sent to where the parcel was first addressed.
                  </div>
                )}
                <Row>
                  <Col label="AP driver login"><input className="input" style={inputStyle} value={fields.ap_driver_login} onChange={(e) => setFields({ ...fields, ap_driver_login: e.target.value })} placeholder="driver login" /></Col>
                  <Col label="Contract" flex="0 0 120px"><input className="input" style={inputStyle} value={fields.contract} onChange={(e) => setFields({ ...fields, contract: e.target.value })} /></Col>
                  <Col label="SLA date" flex="0 0 150px"><input type="date" className="input" style={inputStyle} value={fields.sla_target_date || ''} onChange={(e) => setFields({ ...fields, sla_target_date: e.target.value })} /></Col>
                </Row>
                <Col label="Tracking / article number"><input className="input" style={inputStyle} value={fields.tracking_no} onChange={(e) => setFields({ ...fields, tracking_no: e.target.value })} /></Col>
                <Col label="Key facts (one per line)"><textarea className="input" style={{ ...inputStyle, minHeight: 60 }} value={arrToLines(fields.key_facts)} onChange={(e) => setFields({ ...fields, key_facts: linesToArr(e.target.value) })} /></Col>
                {/* Office-side only since 24 Aug 2026: these auto-drafted questions used to be
                    listed on the driver's portal under "The office needs to know", alongside the
                    questionnaire that asks the real ones. The questionnaire below is now the only
                    place the driver is asked anything; these stay as triage reference. */}
                <Col label="Drafted questions (office reference — not shown to the driver)"><textarea className="input" style={{ ...inputStyle, minHeight: 80 }} value={arrToLines(fields.driver_questions)} onChange={(e) => setFields({ ...fields, driver_questions: linesToArr(e.target.value) })} /></Col>
                <Col label="Quick answers (one per line)"><textarea className="input" style={{ ...inputStyle, minHeight: 70 }} value={arrToLines(fields.quick_answers)} onChange={(e) => setFields({ ...fields, quick_answers: linesToArr(e.target.value) })} /></Col>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(edit || drawer.mode === 'add') ? (
                <>
                  <button className="btn btn-primary" disabled={busy} onClick={save}><Save size={14} /> {drawer.mode === 'add' ? 'Add case' : 'Save changes'}</button>
                  {drawer.mode === 'view' && <button className="btn btn-ghost" disabled={busy} onClick={() => setEdit(false)}>Cancel</button>}
                </>
              ) : row && (
                <>
                  <button className="btn btn-secondary" onClick={() => setEdit(true)}>Edit</button>
                  {row.status === 'open' && <button className="btn btn-primary" disabled={busy} onClick={() => setStatus('resolved')} style={{ background: '#16a34a', borderColor: '#16a34a' }}><CheckCircle2 size={14} /> Mark resolved</button>}
                  {row.status === 'open' && <button className="btn btn-ghost" disabled={busy} onClick={() => setStatus('cancelled')}>Cancel</button>}
                  {row.status !== 'open' && <button className="btn btn-ghost" disabled={busy} onClick={() => setStatus('open')}>Re-open</button>}
                  {isAdmin && <button className="btn btn-ghost" disabled={busy} onClick={del} style={{ color: '#b91c1c', marginLeft: 'auto' }}><Trash2 size={14} /> Delete</button>}
                </>
              )}
            </div>
          </>
        )}
      </div>
      {portalPreview && row && <DriverPortalPreview caseId={row.id} onClose={() => setPortalPreview(false)} />}
    </div>
  );
}

// Colour a rate against the 1-per-1000 target: green at/under, amber up to 2×, red beyond.
const rateColor = (v, target = 1) => (v == null ? 'var(--hp-dim)' : v <= target ? '#16a34a' : v <= target * 2 ? '#d97706' : '#b91c1c');
const reasonList = (obj) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1]).map(([r, n]) => `${r} (${n})`).join(', ');

const todayISO = () => new Date().toLocaleDateString('en-CA');
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - (n - 1)); return d.toLocaleDateString('en-CA'); };
const isDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
const dayISO = (d) => d.toLocaleDateString('en-CA');

// The window picker. Calendar months lead — that's how the business reads its own
// numbers and how AP bills — with the rolling windows kept underneath.
const PRESETS = [
  ['month', 'This month'],
  ['lastmonth', 'Last month'],
  ['custom', 'Custom range…'],
  ['30', 'Last 30 days'],
  ['90', 'Last 90 days'],
  ['180', 'Last 180 days'],
  ['365', 'Last 12 months'],
];
// A preset → [from, to]. "This month" runs to today, not to the end of the month.
function presetRange(v) {
  const now = new Date();
  if (v === 'month') return [dayISO(new Date(now.getFullYear(), now.getMonth(), 1)), todayISO()];
  if (v === 'lastmonth') {
    return [dayISO(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
            dayISO(new Date(now.getFullYear(), now.getMonth(), 0))];   // day 0 = last day of the previous month
  }
  return [daysAgoISO(+v), todayISO()];
}
const monthName = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

function CaseStatsView({ notify }) {
  const [preset, setPreset] = useState('month');
  const [from, setFrom] = useState(() => presetRange('month')[0]);
  const [to, setTo] = useState(() => presetRange('month')[1]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLogin, setOpenLogin] = useState(null);

  const pickPreset = (v) => {
    setPreset(v);
    if (v !== 'custom') { const [a, b] = presetRange(v); setFrom(a); setTo(b); }
  };

  useEffect(() => {
    if (!isDay(from) || !isDay(to)) return; // half-typed custom date — wait
    setLoading(true);
    api('caseStats', { from, to }).then((r) => { if (r.success) setStats(r.stats); else notify?.(r.error || 'Failed', 'error'); }).catch(() => {}).finally(() => setLoading(false));
  }, [from, to, notify]);

  const Metric = ({ label, value }) => (
    <div style={{ flex: '1 1 200px', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 16 }}>
      <div style={{ ...labelStyle, marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: '2rem', fontWeight: 800, color: rateColor(value) }}>{value == null ? '—' : value}</span>
        <span style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>/ 1000 · target ≤ 1.0</span>
      </div>
    </div>
  );

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ ...labelStyle, margin: 0 }}>Window</span>
        <select className="input" style={{ ...inputStyle, width: 170 }} value={preset} onChange={(e) => pickPreset(e.target.value)}>
          {PRESETS.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
        </select>
        <input type="date" className="input" style={{ ...inputStyle, width: 150 }} value={from} max={to || undefined}
          onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }} />
        <span style={{ color: 'var(--hp-dim)' }}>→</span>
        <input type="date" className="input" style={{ ...inputStyle, width: 150 }} value={to} min={from || undefined}
          onChange={(e) => { setPreset('custom'); setTo(e.target.value); }} />
        {stats && <span style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>
          {preset === 'month' ? `${monthName(stats.from)} to date · ` : preset === 'lastmonth' ? `${monthName(stats.from)} · ` : ''}
          {stats.from} → {stats.to}
        </span>}
      </div>

      {loading || !stats ? (
        <div className="card" style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Metric label="Cases per 1000 parcels" value={stats.totals.casesPer1000} />
            <Metric label="At-fault cases per 1000" value={stats.totals.atFaultPer1000} />
            <div style={{ flex: '1 1 200px', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 16, fontSize: '.85rem', color: 'var(--hp-dim)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
              <div><strong style={{ color: 'var(--hp-text)' }}>{stats.totals.cases}</strong> cases · <strong style={{ color: 'var(--hp-text)' }}>{stats.totals.atFault}</strong> at fault{stats.totals.unclassified ? ` · ${stats.totals.unclassified} unclassified` : ''}</div>
              <div>across <strong style={{ color: 'var(--hp-text)' }}>{stats.totals.parcels.toLocaleString()}</strong> parcels delivered</div>
              <div>by <strong style={{ color: 'var(--hp-text)' }}>{stats.totals.drivers ?? stats.rows.length}</strong> drivers</div>
            </div>
          </div>

          <AreaBreakdown areas={stats.areas} />

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--hp-border)', fontWeight: 700, fontSize: '.9rem' }}>Every driver</div>
            {stats.rows.length === 0 ? (
              <div style={{ padding: 26, color: 'var(--hp-dim)' }}>No delivery activity in this window.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                  <thead><tr>
                    <th style={th}>Driver</th><th style={th}>Area</th><th style={{ ...th, textAlign: 'right' }}>Parcels</th>
                    <th style={{ ...th, textAlign: 'right' }}>Cases</th><th style={{ ...th, textAlign: 'right' }}>/ 1000</th>
                    <th style={{ ...th, textAlign: 'right' }}>At fault</th><th style={{ ...th, textAlign: 'right' }}>/ 1000</th>
                  </tr></thead>
                  <tbody>
                    {stats.rows.map((r) => (
                      <React.Fragment key={r.login}>
                        <tr style={{ cursor: 'pointer' }} onClick={() => setOpenLogin(openLogin === r.login ? null : r.login)}>
                          <td style={{ ...td, fontWeight: 600 }}>{r.name || r.login} <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-dim)', fontSize: '.72rem' }}>{r.name ? r.login : ''}</span></td>
                          <td style={{ ...td, color: AREA_COLOR[r.area] || 'var(--hp-dim)', fontWeight: 600, fontSize: '.8rem' }}>{areaLabel(r.area)}</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{r.parcels.toLocaleString()}</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{r.cases}</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: rateColor(r.casesPer1000) }}>{r.casesPer1000 == null ? '—' : r.casesPer1000}</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{r.atFault}</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: rateColor(r.atFaultPer1000) }}>{r.atFaultPer1000 == null ? '—' : r.atFaultPer1000}</td>
                        </tr>
                        {openLogin === r.login && (
                          <tr><td colSpan={7} style={{ ...td, background: 'var(--hp-surface)', fontSize: '.84rem' }}>
                            <div><strong>{r.name || r.login}</strong> — {r.cases} case{r.cases !== 1 ? 's' : ''}: {r.notAtFault} with no error to the driver, {r.atFault} at fault{r.unclassified ? `, ${r.unclassified} not yet classified` : ''}.</div>
                            {r.notAtFault > 0 && <div style={{ marginTop: 4, color: 'var(--hp-dim)' }}>No fault: {reasonList(r.okReasons) || '—'}</div>}
                            {r.atFault > 0 && <div style={{ marginTop: 2, color: '#b91c1c' }}>At fault: {reasonList(r.faultReasons) || '—'}</div>}
                          </td></tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ReasonBreakdown rows={stats.rows} />
        </>
      )}
    </div>
  );
}

// The same figures rolled up per delivery area, so City, Darlinghurst and Broadway can be
// compared side by side. Built server-side from the per-driver rows, so the areas always
// add back up to the totals above. A driver with no route row lands in "Unassigned".
function AreaBreakdown({ areas }) {
  const list = areas || [];
  if (!list.length) return null;
  const worst = Math.max(...list.map((a) => a.casesPer1000 || 0), 0);
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--hp-border)', fontWeight: 700, fontSize: '.9rem' }}>Cases by area</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <thead><tr>
            <th style={th}>Area</th><th style={{ ...th, textAlign: 'right' }}>Drivers</th>
            <th style={{ ...th, textAlign: 'right' }}>Parcels</th>
            <th style={{ ...th, textAlign: 'right' }}>Cases</th><th style={{ ...th, textAlign: 'right' }}>/ 1000</th>
            <th style={{ ...th, textAlign: 'right' }}>At fault</th><th style={{ ...th, textAlign: 'right' }}>/ 1000</th>
            <th style={th}>Share of cases</th>
          </tr></thead>
          <tbody>
            {list.map((a) => {
              const share = worst > 0 ? Math.round(((a.casesPer1000 || 0) / worst) * 100) : 0;
              return (
                <tr key={a.area}>
                  <td style={{ ...td, fontWeight: 700, color: AREA_COLOR[a.area] || 'var(--hp-text)' }}>{areaLabel(a.area)}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{a.drivers}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{a.parcels.toLocaleString()}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{a.cases}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: rateColor(a.casesPer1000) }}>{a.casesPer1000 == null ? '—' : a.casesPer1000}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{a.atFault}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: rateColor(a.atFaultPer1000) }}>{a.atFaultPer1000 == null ? '—' : a.atFaultPer1000}</td>
                  <td style={{ ...td, minWidth: 120 }}>
                    <div title={`${a.casesPer1000 == null ? '—' : a.casesPer1000} per 1000 parcels`} style={{ height: 8, borderRadius: 4, background: 'var(--hp-border)' }}>
                      <div style={{ width: `${share}%`, height: '100%', borderRadius: 4, background: AREA_COLOR[a.area] || 'var(--hp-dim)' }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '10px 14px', borderTop: '1px solid var(--hp-border)', fontSize: '.76rem', color: 'var(--hp-dim)' }}>
        The bar compares each area's case rate against the worst area, not against the target — the numbers either side are the figures that matter.
      </div>
    </div>
  );
}

// Why the cases happen — every recorded fault call rolled up by reason, with the drivers
// behind each one. Repeat offenders (2+ of the same reason) are highlighted so the pattern
// jumps out. Built entirely from the per-driver stats already loaded.
function ReasonBreakdown({ rows }) {
  const roll = (key) => {
    const map = {};
    rows.forEach((r) => Object.entries(r[key] || {}).forEach(([reason, n]) => {
      const b = map[reason] || (map[reason] = { total: 0, drivers: [] });
      b.total += n;
      b.drivers.push({ login: r.login, name: r.name, n });
    }));
    return Object.entries(map)
      .map(([reason, v]) => ({ reason, total: v.total, drivers: v.drivers.sort((a, b) => b.n - a.n) }))
      .sort((a, b) => b.total - a.total);
  };
  const atFault = roll('faultReasons');
  const noFault = roll('okReasons');
  const unclassified = rows.reduce((a, r) => a + (r.unclassified || 0), 0);
  if (!atFault.length && !noFault.length && !unclassified) return null;

  const Section = ({ title, color, items, repeatHighlight }) => (
    <div style={{ flex: '1 1 320px', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 14 }}>
      <div style={{ ...labelStyle, color, marginBottom: 10 }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: '.82rem', color: 'var(--hp-dim)' }}>None in this window.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(({ reason, total, drivers }) => (
            <div key={reason}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: '.86rem', fontWeight: 700 }}>{reason}</span>
                <span style={{ fontSize: '.78rem', fontFamily: 'var(--font-mono)', color }}>{total}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                {drivers.map((d) => {
                  const repeat = repeatHighlight && d.n >= 2;
                  return (
                    <span key={d.login} title={repeat ? `${d.n}× the same reason — a pattern` : undefined}
                      style={{ fontSize: '.72rem', padding: '2px 8px', borderRadius: 12, border: `1px solid ${repeat ? color : 'var(--hp-border)'}`, background: repeat ? `${'rgba(185,28,28,0.08)'}` : 'var(--hp-surface)', fontWeight: repeat ? 700 : 400, color: repeat ? color : 'var(--hp-text)' }}>
                      {d.name || d.login}{d.n > 1 ? ` ×${d.n}` : ''}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...labelStyle, marginBottom: 10 }}>Reason breakdown</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Section title="At fault — what went wrong" color="#b91c1c" items={atFault} repeatHighlight />
        <Section title="No fault — why the case cleared" color="#16a34a" items={noFault} repeatHighlight={false} />
      </div>
      {unclassified > 0 && (
        <div style={{ fontSize: '.78rem', color: '#d97706', marginTop: 8 }}>
          {unclassified} case{unclassified !== 1 ? 's' : ''} in this window not yet given a fault call — they're missing from the breakdown above.
        </div>
      )}
    </div>
  );
}

const Chip = ({ addr, onX, strong }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.74rem', padding: '3px 8px', borderRadius: 14, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontWeight: strong ? 700 : 400 }}>
    {addr}
    <span onClick={onX} style={{ cursor: 'pointer', color: 'var(--hp-dim)', fontWeight: 700 }} title="Remove">×</span>
  </span>
);

const Row = ({ children }) => <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{children}</div>;
const Col = ({ label, children, flex = '1 1 120px' }) => (
  <div style={{ flex }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);
